/**
 * BTA Boarding - Single File Bundle
 * Kennel Club Boarding - kennelclubboarding.ca  
 * Version: 4.7.2 - Boarding night-count correction (Nov 3 2026)
 *
 * New in 4.7.2:
 * - ROOT CAUSE (order #13060, Nov 3 2026): a BookThatApp widget update sends
 *   /cart/add.js with quantity=1 for every boarding stay, even though its own
 *   Start/Finish dates are correct. Boarding is priced per night (quantity =
 *   nights), so a 4-night stay was charged as 1 night. Affects every boarding
 *   variant and the Long-Term Boarding page (same script).
 * - The fetch interceptor now works out the nights from BTA's own Start/Finish
 *   (ISO timestamps as fallback) and sets the request's quantity to match
 *   before it is sent. If BTA already sends the right quantity, nothing
 *   changes. Every correction is logged to telemetry as 'bta_qty_corrected'.
 * - Runs before (and independently of) the time-property injection, so it
 *   applies even if the time selections are missing.
 *
 * Version: 4.7.1 - Flex modal Confirm always visible (Oct 31 2026)
 *
 * New in 4.7.1:
 * - Flex modal fix (Oct 31 2026): on phones the card was capped at 94vh with
 *   overflow hidden, so Confirm was cut off on shorter screens (iPhone SE/16,
 *   Galaxy A55). The card is now sized to the visible screen (visualViewport),
 *   the content scrolls, and Booking total + Confirm sit in a pinned footer
 *   that is always on screen.
 *
 * Version: 4.7 - BTA selection verification (Oct 2026)
 *
 * New in 4.7:
 * - ROOT CAUSE (order #13004): the script tracks drop-off/pick-up by COUNTING
 *   taps, while BTA keeps its own range. Any tap the script misses (page
 *   still loading, widget rebuild gap, sold-out-next-day path) leaves BTA
 *   one tap ahead, so the customer's "drop-off" tap becomes the END of a
 *   range starting weeks earlier (huge price) and the next tap restarts it.
 * - After every drop-off / pick-up tap the script now READS BTA's real
 *   selection from the calendar (rdp-day_range_start / rdp-day_range_end)
 *   and compares. On mismatch it re-taps the date internally (BTA restarts
 *   its range on that tap — verified live 2026-10-28) and re-checks. If it
 *   still can't match, the customer gets a "Re-select your dates" modal that
 *   clears the calendar instead of a wrong booking.
 * - Next button: final check that the visible calendar matches the
 *   script's dates before the Flex modal / cart; mismatch -> re-select modal.
 * - Sold-out-next-day drop-off click is now blocked before BTA sees it.
 * - Iframe watcher checks every 250ms (was 1000ms) to shrink the gap.
 * - Flex modal now shows the stay (drop-off, pick-up, nights, booking total)
 *   with a "These aren't my dates - start over" link.
 * - Telemetry: bta_desync, bta_repaired, bta_resync_modal, next_check_failed,
 *   flex_startover (notable); btaCheck (routine).
 *
 * Version: 4.6 - BTA widget rebuild compatibility (Sept 2026)
 *
 * New in 4.6:
 * - BTA rebuilt its widget; the hashed price class (span.hXgXeP) is gone.
 *   Price is now .price-container span.ml10 (stable, non-hashed classes).
 *   Added PRICE_SELECTORS fallback chain + findPriceElement(doc), used by
 *   getBookingAmountFromIframe, injectAvailabilityBadge, injectWaitlistButton.
 *   All previous selectors and the text-scan fallback are retained.
 * - No behavioural changes.
 *
 * Version: 4.5 - Range-restart confirmation modal
 *
 * New in 4.5:
 * - When a customer with a COMPLETED selection (both dates + times) clicks a
 *   date, the click is now intercepted BEFORE it reaches BTA (capture phase,
 *   same mechanism as the restricted-date flow). The calendar keeps showing
 *   the existing selection and a confirmation modal appears stating the
 *   current selection and asking whether to start over with the clicked date
 *   as the new drop-off.
 *   - "Keep my current selection" → click discarded, nothing changes.
 *   - "Start over with new dates" → script state cleared, the click is
 *     re-fired programmatically (one-shot bypass flag) so BTA restarts its
 *     range normally and the drop-off time modal follows — script and BTA
 *     stay in sync with the customer's explicit consent.
 * - Eliminates the "my dates disappeared" experience found in the customer
 *   trace (kcbtel_...k2zws7) and reproduced in testing.
 * - Telemetry: restart_guard_shown / restart_guard_keep events; the
 *   range_restart notable now includes via:'modal' when consented here.
 * - v4.4's silent resetTimeSelections() in the first-click branches is
 *   retained as a safety net for paths the guard doesn't cover.
 *
 * Version: 4.4 - Range-restart state sync + telemetry dedup
 *
 * New in 4.4:
 * - FIX A: when a date click starts a NEW selection while a completed prior
 *   selection exists in timeState, BTA restarts its range internally — the
 *   script now calls resetTimeSelections() to match, so the missing-pickup
 *   modal fires correctly if the user proceeds without re-selecting a
 *   pick-up (previously the stale pick-up suppressed it and BTA's raw
 *   "Please select finish date" tooltip was the only feedback).
 *   Applied to both the normal and Dec-24 AM-only first-click branches.
 * - New notable telemetry event 'range_restart' so these occurrences are
 *   captured (previously invisible: the suppressed modal was the only
 *   notable trigger on this path).
 * - FIX B: pagehide/hidden telemetry flushes are skipped when no new events
 *   occurred since the last transmission — eliminates duplicate rows from
 *   long-lived tabs.
 *
 * Version: 4.3 - Session telemetry for date-shift diagnosis
 *
 * New in 4.3:
 * - Added lightweight session telemetry (tel* functions). Buffers key events
 *   (init, listener attach, watcher rebuilds, date clicks, time modals,
 *   dropdown navigation, missing-pickup modal, cart-add dates) in memory.
 * - Transmits ONLY when something notable happens (date mismatch, watcher
 *   reset mid-selection, missing-pickup modal, dropdown navigation), sending
 *   the FULL session trace for context via navigator.sendBeacon to the
 *   Combined.gs web app (mode=btaTelemetry).
 * - Includes _bookingSessionId in cart-add events so telemetry rows can be
 *   joined to Shopify orders.
 * - Every telemetry call is wrapped in try/catch and gated by
 *   TELEMETRY_CONFIG.enabled — telemetry can never break a booking.
 *
 * Version: 4.2 - Cart dates now derived from BTA's own properties
 *
 * New in 4.2:
 * - dropOffDate/dropOffDay/pickUpDate/pickUpDay and time windows are now
 *   derived from BTA's authoritative _start_iso8601/_finish_iso8601 (or
 *   Start/Finish) properties found in the same /cart/add.js FormData,
 *   instead of the click-tracked timeState dates. Fixes desync where the
 *   script missed/double-counted a calendar tap and wrote wrong dates to
 *   the cart while BTA itself had the correct booking (e.g. order #12175).
 * - Falls back to previous timeState behaviour if BTA properties are
 *   missing or unparseable; logs a warning when the two sources disagree.
 * - Finish end-of-day correction (T03:59:59Z / T04:59:59Z → minus one day)
 *   done in pure UTC arithmetic so customer timezone can never skew it.
 * - Iframe watcher now also calls resetTimeSelections() on widget rebuild
 *   so a stale pre-rebuild drop-off can't pair with a fresh pick-up.
 *
 * New in 4.1:
 * - Added injectWaitlistButton() — outlined gold-bordered button below price
 * - Always visible inside the BTA widget for boarding
 * - Reuses CONFIG.waitlistUrl (https://kennelclubboarding.ca/pages/boarding-waitlist)
 * - Idempotent (skips if #bta-waitlist-button already present)
 * - Called alongside injectAvailabilityBadge in installEventListeners and the iframe watcher
 *
 * New in 4.0:
 * - Added FLEX_CONFIG with variant IDs, tier rates, fee floor and calculation helpers
 * - Added flexState to track customer Flex decision per session
 * - Added getBookingAmountFromIframe() to read booking price from BTA widget
 * - Added showFlexModal() — renders Flex Booking opt-in/opt-out modal before Next proceeds
 * - Modified Next button listener to show Flex modal before proceeding to time/radio selection
 * - Modified fetch interceptor to inject _flexProtection line property into cart/add.js
 * - flexState resets when iframe watcher detects widget rebuild
 *
 * New in 3.9:
 * - Removed iframe document caching — always gets a fresh reference
 * - handleDateClick now uses a fresh iframe doc on every call
 * - Added persistent iframe watcher that detects when BTA rebuilds its widget
 *   and automatically re-attaches the click listener, fixing the "hit or miss"
 *   behaviour when returning to the page or after BTA resets
 *
 * New in 3.8:
 * - Added real-time availability badge (below calendar, above price)
 * 
 * New in 3.7:
 * - Added modal when user clicks Next without selecting pick-up date
 * - Modal explains how to use < > arrows to navigate between months
 * - Automatically scrolls calendar into view (for mobile)
 * - Animates/highlights the navigation arrows to draw attention
 * 
 * New in 3.6:
 * - Fixed issue where Next button listener was lost after navigating to different month
 * - Now gets fresh iframe document reference on each check instead of using cached version
 * - Tracks actual button element to detect when DOM is rebuilt
 * 
 * New in 3.5:
 * - Fixed issue where dates could get swapped if user clicked calendar odd number of times
 * - Now ensures drop-off is always the earlier date and pick-up is always the later date
 * - Dates are validated and corrected (if needed) when Next button is clicked
 * 
 * New in 3.4:
 * - After drop-off selection, detects sold-out dates and disables all dates from first sold-out onward
 * - Modal when user tries to select pick-up spanning sold-out dates, lists ALL sold-out dates in range  
 * - "Start Over" button to reset and choose a different drop-off date
 * - Works across month boundaries
 */
(function() {
'use strict';

// ============================================================================
// BTA DOM SELECTORS (v4.6) — fallback chains, most specific/current first
// BTA rebuilds its widget periodically; hashed styled-components classes change.
// ============================================================================
const PRICE_SELECTORS = [
  '.price-container span.ml10',            // Sept 2026 build (stable non-hashed classes)
  'span.sc-iHGNWf.sc-carFqZ.hXgXeP',       // Pre-Sept 2026 build
  'span.hXgXeP'                            // Pre-Sept 2026 build (short)
];

function findPriceElement(doc) {
  if (!doc) return null;
  for (const sel of PRICE_SELECTORS) {
    const el = doc.querySelector(sel);
    if (el) return el;
  }
  return null;
}

// ============================================================================
// ⚙️ CONFIGURATION - CHANGE SETTINGS HERE
// ============================================================================

/**
 * LOGGING TOGGLE
 * Set to false to disable all console logging in production
 * Set to true to enable detailed console logging for debugging
 */
const ENABLE_LOGGING = false;  // ← CHANGE THIS TO true/false

// ============================================================================
// SHARED STATE & CONFIG
// ============================================================================

const CONFIG = {
  highlightColor: '#BD9B60',

  // Holiday restrictions
  restrictions: {
    dec24: { month: 12, day: 24, type: 'AM_ONLY' },
    dec25: { month: 12, day: 25, type: 'NO_SERVICE' },
    dec26: { month: 12, day: 26, type: 'NO_SERVICE' },
    jan1: { month: 1, day: 1, type: 'NO_SERVICE' }
  },

  // URLs for waitlist and account creation
  waitlistUrl: 'https://kennelclubboarding.ca/pages/boarding-waitlist',
  registerUrl: 'https://kennelclubboarding.ca/account/register'
};

// ============================================================================
// FLEX BOOKING CONFIGURATION
// ============================================================================

const FLEX_CONFIG = {
  // Shopify variant IDs for each Flex Booking product ($0.01 each, qty = fee in cents)
  // Assigned in order to each distinct booking line item in the cart
  flexVariantIds: [
    49718668984557,  // Flex Booking Protection
    49718669246701,  // Flex Booking Protection 2
    49718669410541,  // Flex Booking Protection 3
    49718669508845,  // Flex Booking Protection 4
    49718669902061,  // Flex Booking Protection 5
  ],

  // $0 product added when customer actively declines protection
  // Appears in confirmation email so customer and merchant can see the decision
  noProtectionVariantId: 49732858446061,  // No Booking Protection (Customer Declined Protection)

  // Fixed fee products — available for manual use in Shopify admin when needed
  cancellationFeeVariantId: 49719101718765,  // Cancellation Fee — $5.00
  transferFeeVariantId:     49718671474925,  // Transfer Fee — $2.50

  // Fee tier structure: [maxOrderValue, percentage]
  // Inverse scale — higher orders pay a lower percentage
  // Raised 7% from original tiers — no cancellation or transfer fees charged to customer
  // 2026-06: top three tiers raised (large boarding only): $400-600 2.35->3.00, $600-1000 1.50->2.75, $1000+ 1.07->2.25
  feeTiers: [
    [50,       0.0481],
    [100,      0.0535],
    [200,      0.0449],
    [400,      0.0375],
    [600,      0.0300],
    [1000,     0.0275],
    [Infinity, 0.0225],
  ],

  // Minimum Flex fee regardless of order amount
  feeFloor: 1.50,

  // Calculate the Flex fee for a given order amount
  // Returns fee rounded to nearest $0.25
  calculateFee(orderAmount) {
    const rate = this.getTierRate(orderAmount);
    const exact = Math.max(this.feeFloor, orderAmount * rate);
    return Math.round(exact * 4) / 4;
  },

  // Get the applicable percentage rate for an order amount
  getTierRate(amount) {
    for (const [max, pct] of this.feeTiers) {
      if (amount <= max) return pct;
    }
    return 0.0107;
  },

  // Convert a fee amount to a quantity of $0.01 variants
  feeToQuantity(fee) {
    return Math.round(fee * 100);
  }
};

// ============================================================================
// FLEX STATE - tracks customer decision for this booking session
// ============================================================================

const flexState = {
  decision: null,   // 'yes' or 'no'
  shown: false,     // whether modal has been shown this session
  sessionId: null,  // unique ID for this booking

  reset() {
    this.decision = null;
    this.shown = false;
    this.sessionId = null;
    log('flexState reset');
  }
};

const state = {
  // Note: iframe/iframeDoc are intentionally NOT cached here.
  // We always fetch a fresh reference to avoid stale doc issues when BTA rebuilds its widget.
  clickCount: 0,
  firstSelectedDate: null,
  modalActive: false,
  pendingSuggestedDate: null,
  // v4.5: one-shot flag — set when the customer confirms "start over" in the
  // range-restart modal so the programmatically re-fired click bypasses the
  // guard and flows through the normal first-click path
  bypassRestartGuard: false,
  // v4.7: set while the script itself taps a calendar date (repair taps) so
  // the click handler ignores it instead of counting it as a customer tap
  internalTap: false,
  // v4.7: incremented on every scheduled BTA check; stale checks bail out
  verifyToken: 0,
  // Sold-out cascade
  soldOutDatesInRange: [],        // Array of sold-out date objects found after drop-off
  calendarObserver: null,         // MutationObserver for calendar changes
  // Watcher state
  watcherInterval: null,          // Interval ID for the persistent iframe watcher
  lastListenerDoc: null,          // Tracks which doc the click listener is currently attached to
  nextButtonInterval: null        // Interval ID for checkForNextButton - tracked to prevent stacking
};

const timeState = {
  dropOffTime: null,
  dropOffDate: null,
  pickUpTime: null,
  pickUpDate: null,
  nextButtonListener: null
};

// ============================================================================
// LOGGING FUNCTIONS
// ============================================================================

function log(message, data = null) {
  if (!ENABLE_LOGGING) return;

  if (data) {
    console.log(`[BTA Boarding] ${message}`, data);
  } else {
    console.log(`[BTA Boarding] ${message}`);
  }
}

// ============================================================================
// SESSION TELEMETRY (v4.3)
// Buffers key events in memory and transmits the full session trace to the
// Combined.gs web app ONLY when something notable happens. Fire-and-forget
// via sendBeacon; every call is try/catch-wrapped so telemetry can never
// break a booking. Kill switch: TELEMETRY_CONFIG.enabled = false.
// ============================================================================

const TELEMETRY_CONFIG = {
  enabled: true,
  // Combined.gs web app deployment, mode=btaTelemetry (doPost route)
  endpoint: 'https://script.google.com/macros/s/AKfycbyIxJqVHCZEkztFyPC64JW2DVUrT218-4sovpV2vt-QhI7QiTjgtyzgSD9xynZVdjw1/exec?mode=btaTelemetry',
  maxEvents: 300,       // hard cap on buffered events per session
  maxFlushes: 5,        // hard cap on transmissions per session
  flushDebounceMs: 1500 // wait for closely-spaced notable events to batch
};

const telState = {
  sessionId: 'kcbtel_' + Date.now() + '_' + Math.random().toString(36).substr(2, 6),
  events: [],
  notable: false,
  notableReasons: [],
  flushCount: 0,
  lastFlushedCount: 0,
  flushTimer: null,
  unloadHooked: false
};

function telEvent(type, data) {
  try {
    if (!TELEMETRY_CONFIG.enabled) return;
    if (telState.events.length >= TELEMETRY_CONFIG.maxEvents) return;
    telState.events.push({ t: Date.now(), type: type, data: data || null });
  } catch (err) { /* telemetry must never throw */ }
}

function telNotable(reason, data) {
  try {
    if (!TELEMETRY_CONFIG.enabled) return;
    telEvent('notable:' + reason, data);
    telState.notable = true;
    if (telState.notableReasons.indexOf(reason) === -1) {
      telState.notableReasons.push(reason);
    }
    telHookUnload();
    // Debounce so a burst of notable events sends one beacon, not several
    if (telState.flushTimer) clearTimeout(telState.flushTimer);
    telState.flushTimer = setTimeout(function() { telSend('debounced'); }, TELEMETRY_CONFIG.flushDebounceMs);
  } catch (err) { /* telemetry must never throw */ }
}

function telSend(trigger) {
  try {
    if (!TELEMETRY_CONFIG.enabled) return;
    if (!telState.notable) return;                                // only notable sessions transmit
    if (telState.events.length === 0) return;
    if (telState.flushCount >= TELEMETRY_CONFIG.maxFlushes) return;
    // v4.4 FIX B: unload-driven flushes only transmit if something new
    // happened since the last send — prevents duplicate rows every time a
    // long-lived tab is hidden or revisited
    if ((trigger === 'pagehide' || trigger === 'hidden') &&
        telState.events.length === telState.lastFlushedCount) {
      return;
    }
    telState.flushCount++;
    telState.lastFlushedCount = telState.events.length;
    if (telState.flushTimer) { clearTimeout(telState.flushTimer); telState.flushTimer = null; }

    const payload = JSON.stringify({
      sessionId: telState.sessionId,
      trigger: trigger,
      notableReasons: telState.notableReasons,
      flushNumber: telState.flushCount,
      page: (window.location && window.location.pathname) || '',
      userAgent: (navigator.userAgent || '').substring(0, 300),
      viewport: (window.innerWidth || 0) + 'x' + (window.innerHeight || 0),
      eventCount: telState.events.length,
      events: telState.events
    });

    let sent = false;
    if (navigator.sendBeacon) {
      try {
        sent = navigator.sendBeacon(TELEMETRY_CONFIG.endpoint, new Blob([payload], { type: 'text/plain' }));
      } catch (beaconErr) { sent = false; }
    }
    if (!sent) {
      // Fallback: keepalive fetch, fire-and-forget (no-cors — we never read the response)
      fetch(TELEMETRY_CONFIG.endpoint, {
        method: 'POST',
        mode: 'no-cors',
        keepalive: true,
        headers: { 'Content-Type': 'text/plain' },
        body: payload
      }).catch(function() { /* fire-and-forget */ });
    }
    log('Telemetry: session trace sent (trigger=' + trigger + ', events=' + telState.events.length + ')');
  } catch (err) { /* telemetry must never throw */ }
}

function telHookUnload() {
  try {
    if (telState.unloadHooked) return;
    telState.unloadHooked = true;
    // Last-chance flush if the customer leaves mid-session after a notable event
    window.addEventListener('pagehide', function() { telSend('pagehide'); });
    document.addEventListener('visibilitychange', function() {
      if (document.visibilityState === 'hidden') telSend('hidden');
    });
  } catch (err) { /* telemetry must never throw */ }
}

// ============================================================================
// BTA-AUTHORITATIVE DATE DERIVATION (v4.2)
// BTA writes its own Start/Finish properties into the same /cart/add.js
// FormData we intercept. Those reflect BTA's actual internal booking state,
// so they are the single source of truth for dates. These helpers extract
// them; the interceptor prefers them over the click-tracked timeState dates.
// ============================================================================

function parseIsoUtcDateParts(isoStr) {
  if (!isoStr) return null;
  const m = String(isoStr).match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/);
  if (!m) return null;
  return {
    year: parseInt(m[1], 10),
    month: parseInt(m[2], 10),   // 1-12
    day: parseInt(m[3], 10),
    hour: parseInt(m[4], 10),
    minute: parseInt(m[5], 10),
    second: parseInt(m[6], 10)
  };
}

function parseSlashDate(str) {
  // Parses BTA's display format "MM/DD/YYYY" into our custom date object.
  if (!str) return null;
  const m = String(str).trim().match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (!m) return null;
  const monthIndex = parseInt(m[1], 10) - 1;
  if (monthIndex < 0 || monthIndex > 11) return null;
  return {
    day: parseInt(m[2], 10),
    month: getMonthName(monthIndex),
    monthIndex: monthIndex,
    year: parseInt(m[3], 10)
  };
}

function deriveDatesFromBtaFormData(formData) {
  try {
    let dropOff = null;
    let pickUp = null;

    // Preferred source: ISO timestamps. Start is midnight ET, which lands on
    // the same UTC calendar date (T04:00:00Z / T05:00:00Z), so the UTC date
    // is the real date.
    const startIso = parseIsoUtcDateParts(formData.get('properties[_start_iso8601]'));
    if (startIso) {
      dropOff = {
        day: startIso.day,
        month: getMonthName(startIso.month - 1),
        monthIndex: startIso.month - 1,
        year: startIso.year
      };
    }

    const finishIso = parseIsoUtcDateParts(formData.get('properties[_finish_iso8601]'));
    if (finishIso) {
      // BTA encodes finish as end-of-day ET, which appears as 03:59:59Z (EDT)
      // or 04:59:59Z (EST) on the FOLLOWING UTC day — subtract one day to get
      // the real last date. Pure UTC arithmetic: the customer's own timezone
      // can never skew this.
      let ms = Date.UTC(finishIso.year, finishIso.month - 1, finishIso.day);
      if ((finishIso.hour === 3 || finishIso.hour === 4) && finishIso.minute === 59 && finishIso.second === 59) {
        ms -= 24 * 60 * 60 * 1000;
      }
      const d = new Date(ms);
      pickUp = {
        day: d.getUTCDate(),
        month: getMonthName(d.getUTCMonth()),
        monthIndex: d.getUTCMonth(),
        year: d.getUTCFullYear()
      };
    }

    // Fallback source: BTA's display-format properties (already end-of-day
    // corrected by BTA itself).
    if (!dropOff) dropOff = parseSlashDate(formData.get('properties[Start]'));
    if (!pickUp) pickUp = parseSlashDate(formData.get('properties[Finish]'));

    if (!dropOff || !pickUp) return null;
    return { dropOff: dropOff, pickUp: pickUp };
  } catch (err) {
    log('deriveDatesFromBtaFormData: error while parsing BTA properties', err);
    return null;
  }
}

// ============================================================================
// BOARDING NIGHT-COUNT CORRECTION (v4.7.2)
// Boarding is priced per night: the cart line quantity must equal the number
// of nights. Since Nov 3 2026 BTA sends quantity=1 regardless of the stay.
// This computes nights from BTA's own dates in the same FormData and sets the
// quantity to match. No-op when BTA's quantity is already correct.
// ============================================================================

function correctBoardingQuantity(formData) {
  try {
    // Only touch booking lines that carry BTA dates
    const startStr = formData.get('properties[Start]');
    const finishStr = formData.get('properties[Finish]');
    let start = parseSlashDate(startStr);
    let finish = parseSlashDate(finishStr);

    // Fallback: ISO timestamps (already end-of-day corrected by the helper)
    if (!start || !finish) {
      const derived = deriveDatesFromBtaFormData(formData);
      if (!derived) {
        log('Night-count check: no BTA dates in request — quantity left unchanged');
        return;
      }
      start = derived.dropOff;
      finish = derived.pickUp;
    }

    // Units > 1 would mean a different pricing model — never guess, leave it
    const units = formData.get('properties[Units]');
    if (units && String(units).trim() !== '1') {
      log(`Night-count check: Units=${units} (not 1) — quantity left unchanged`);
      return;
    }

    const nights = Math.round(
      (Date.UTC(finish.year, finish.monthIndex, finish.day) -
       Date.UTC(start.year, start.monthIndex, start.day)) / 86400000
    );

    // Sanity range: at least 1 night, at most a year
    if (!Number.isFinite(nights) || nights < 1 || nights > 366) {
      log(`Night-count check: computed ${nights} nights — outside 1..366, quantity left unchanged`);
      return;
    }

    const currentQty = parseInt(formData.get('quantity'), 10);
    if (currentQty === nights) {
      log(`Night-count check: quantity ${currentQty} already matches ${nights} night(s)`);
      return;
    }

    formData.set('quantity', String(nights));
    log(`⚠ Night-count corrected: BTA sent quantity ${currentQty} → set to ${nights} night(s) (${startStr} → ${finishStr})`);
    telNotable('bta_qty_corrected', {
      from: Number.isFinite(currentQty) ? currentQty : null,
      to: nights,
      start: startStr || null,
      finish: finishStr || null
    });
  } catch (err) {
    log('Night-count check: error — quantity left unchanged', err);
  }
}

// ============================================================================
// FETCH INTERCEPTOR - Inject custom properties into cart/add.js
// ============================================================================

const originalFetch = window.fetch;
window.fetch = function(...args) {
  const url = args[0];
  const options = args[1];

  // Intercept /cart/add.js requests
  if (url === '/cart/add.js' && options?.body instanceof FormData) {
    log('═══════════════════════════════════════════════════════════');
    log('🛒 Intercepting /cart/add.js request');

    // v4.7.2: make quantity = nights before anything else (independent of
    // whether time selections are available below)
    correctBoardingQuantity(options.body);

    // Check if we have time selections to inject
    if (timeState.dropOffTime && timeState.pickUpTime && timeState.dropOffDate && timeState.pickUpDate) {
      const formData = options.body;

      // v4.2 FIX: prefer BTA's own dates from this same FormData. The
      // click-tracked timeState dates can desync from BTA (missed or
      // double-counted taps, widget rebuilds mid-selection); BTA's
      // properties always reflect the booking BTA will actually create.
      const btaDates = deriveDatesFromBtaFormData(formData);
      const dropOffSource = (btaDates && btaDates.dropOff) ? btaDates.dropOff : timeState.dropOffDate;
      const pickUpSource = (btaDates && btaDates.pickUp) ? btaDates.pickUp : timeState.pickUpDate;

      if (btaDates) {
        const mismatch =
          btaDates.dropOff.year !== timeState.dropOffDate.year ||
          btaDates.dropOff.monthIndex !== timeState.dropOffDate.monthIndex ||
          btaDates.dropOff.day !== timeState.dropOffDate.day ||
          btaDates.pickUp.year !== timeState.pickUpDate.year ||
          btaDates.pickUp.monthIndex !== timeState.pickUpDate.monthIndex ||
          btaDates.pickUp.day !== timeState.pickUpDate.day;
        if (mismatch) {
          log('⚠ DATE MISMATCH: timeState disagrees with BTA properties — using BTA dates');
          log(`  timeState: dropOff=${timeState.dropOffDate.month} ${timeState.dropOffDate.day}, ${timeState.dropOffDate.year} | pickUp=${timeState.pickUpDate.month} ${timeState.pickUpDate.day}, ${timeState.pickUpDate.year}`);
          log(`  BTA:       dropOff=${btaDates.dropOff.month} ${btaDates.dropOff.day}, ${btaDates.dropOff.year} | pickUp=${btaDates.pickUp.month} ${btaDates.pickUp.day}, ${btaDates.pickUp.year}`);
          telNotable('date_mismatch', {
            timeState: {
              dropOff: `${timeState.dropOffDate.year}-${timeState.dropOffDate.monthIndex + 1}-${timeState.dropOffDate.day}`,
              pickUp: `${timeState.pickUpDate.year}-${timeState.pickUpDate.monthIndex + 1}-${timeState.pickUpDate.day}`
            },
            bta: {
              dropOff: `${btaDates.dropOff.year}-${btaDates.dropOff.monthIndex + 1}-${btaDates.dropOff.day}`,
              pickUp: `${btaDates.pickUp.year}-${btaDates.pickUp.monthIndex + 1}-${btaDates.pickUp.day}`
            }
          });
        }
      } else {
        log('⚠ Could not derive dates from BTA properties — falling back to timeState dates');
        telNotable('bta_dates_unparseable', null);
      }

      // Convert custom date objects to JavaScript Date objects
      const dropOffDate = new Date(dropOffSource.year, dropOffSource.monthIndex, dropOffSource.day);
      const pickUpDate = new Date(pickUpSource.year, pickUpSource.monthIndex, pickUpSource.day);

      // Get day names
      const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      const dropOffDay = days[dropOffDate.getDay()];
      const pickUpDay = days[pickUpDate.getDay()];

      // Format dates
      const formatDate = (d) => `${d.month} ${d.day}, ${d.year}`;
      const dropOffDateStr = formatDate(dropOffSource);
      const pickUpDateStr = formatDate(pickUpSource);

      // Get time windows (need to reference getTimeWindows after it's defined)
      let dropOffTimeWindow, pickUpTimeWindow;
      if (typeof getTimeWindows === 'function') {
        const dropOffWindows = getTimeWindows(dropOffDate, false);
        const pickUpWindows = getTimeWindows(pickUpDate, true);
        dropOffTimeWindow = dropOffWindows[timeState.dropOffTime];
        pickUpTimeWindow = pickUpWindows[timeState.pickUpTime];
      } else {
        // Fallback if getTimeWindows not available yet
        dropOffTimeWindow = { start: timeState.dropOffTime === 'AM' ? '7:00am' : '12:00pm', end: timeState.dropOffTime === 'AM' ? '12:00pm' : '6:00pm' };
        pickUpTimeWindow = { start: timeState.pickUpTime === 'AM' ? '7:00am' : '12:00pm', end: timeState.pickUpTime === 'AM' ? '12:00pm' : '6:00pm' };
      }

      // Add our custom properties
      formData.append('properties[dropOffTime]', `${dropOffTimeWindow.start} - ${dropOffTimeWindow.end}`);
      formData.append('properties[dropOffDay]', dropOffDay);
      formData.append('properties[dropOffDate]', dropOffDateStr);
      formData.append('properties[pickUpTime]', `${pickUpTimeWindow.start} - ${pickUpTimeWindow.end}`);
      formData.append('properties[pickUpDay]', pickUpDay);
      formData.append('properties[pickUpDate]', pickUpDateStr);

      // Private property — used by cart-kcb-enhancements.js to add/suppress Flex cart items
      const flexValue = flexState.decision === 'yes' ? 'Yes' : 'No';
      formData.append('properties[_flexProtection]', flexValue);

      // Unique booking session ID — ties this booking to its Flex cart item
      if (!flexState.sessionId) {
        flexState.sessionId = 'kcb_' + Date.now() + '_' + Math.random().toString(36).substr(2, 6);
      }
      formData.append('properties[_bookingSessionId]', flexState.sessionId);
      log(`  _bookingSessionId: ${flexState.sessionId}`);

      // Private property — calculated Flex fee in dollars, read by cart to set variant quantity
      // Only injected when customer chose protection; cart uses this to set qty = fee * 100
      if (flexState.decision === 'yes') {
        // Read booking amount from the existing formData price if available,
        // otherwise fall back to getBookingAmountFromIframe
        const freshDocForFee = getIframeDoc();
        const bookingAmountForFee = getBookingAmountFromIframe(freshDocForFee);
        if (bookingAmountForFee) {
          const calculatedFee = FLEX_CONFIG.calculateFee(bookingAmountForFee);
          formData.append('properties[_flexFee]', calculatedFee.toFixed(2));
          log(`  _flexFee: ${calculatedFee.toFixed(2)}`);
        }
      }

      // Public property — visible to customer in confirmation email and order history
      // Makes protection status unambiguous for both customer and merchant
      const flexPublicValue = flexState.decision === 'yes'
        ? '✓ Protected — Refundable & Transferable'
        : '✕ No Protection — Non-Refundable & Non-Transferable';
      formData.append('properties[Booking Protection]', flexPublicValue);

      log(`  _flexProtection: ${flexValue}`);
      log(`  Booking Protection: ${flexPublicValue}`);

      log('✓ Injected custom properties:');
      log(`  dropOffTime: ${dropOffTimeWindow.start} - ${dropOffTimeWindow.end}`);
      log(`  dropOffDay: ${dropOffDay}`);
      log(`  dropOffDate: ${dropOffDateStr}`);
      log(`  pickUpTime: ${pickUpTimeWindow.start} - ${pickUpTimeWindow.end}`);
      log(`  pickUpDay: ${pickUpDay}`);
      log(`  pickUpDate: ${pickUpDateStr}`);

      // v4.3 telemetry: record the final written dates; join key = _bookingSessionId
      telEvent('cartAdd', {
        dropOffDate: dropOffDateStr,
        pickUpDate: pickUpDateStr,
        source: btaDates ? 'bta' : 'timeState',
        bookingSessionId: flexState.sessionId || null
      });
      telSend('cart_add');
    } else {
      log('⚠ No time selections available to inject');
    }
    log('═══════════════════════════════════════════════════════════');
  }

  return originalFetch.apply(this, args);
};

log('✓ Fetch interceptor installed for /cart/add.js');

log('═══════════════════════════════════════════════════════════');
log('BTA Boarding Script loading...');
log('Version: 3.7 - With Missing Pick-Up Date Modal');
log('Timestamp: ' + new Date().toISOString());
log('═══════════════════════════════════════════════════════════');
log('CONFIG loaded:', CONFIG);
log('State initialized:', state);

// ============================================================================
// TIME WINDOW CONFIGURATION
// ============================================================================

const TIME_CONFIG = {
  // Drop-off times
  weekdayAM: { start: '7:00 AM', end: '8:45 AM' },
  weekdayPM: { start: '4:00 PM', end: '5:45 PM' },
  weekendAM: { start: '8:00 AM', end: '8:45 AM' },
  weekendPM: { start: '5:00 PM', end: '5:45 PM' },

  // Pick-up times (same as drop-off)
  pickupWeekdayAM: { start: '7:00 AM', end: '8:45 AM' },
  pickupWeekdayPM: { start: '4:00 PM', end: '5:45 PM' },
  pickupWeekendAM: { start: '8:00 AM', end: '8:45 AM' },
  pickupWeekendPM: { start: '5:00 PM', end: '5:45 PM' }
};

// ============================================================================
// HOLIDAY CALCULATIONS (for time windows)
// ============================================================================

function getGoodFriday(year) {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;

  const easter = new Date(year, month - 1, day);
  const goodFriday = new Date(easter);
  goodFriday.setDate(easter.getDate() - 2);
  return goodFriday;
}

function getVictoriaDay(year) {
  // The Monday PRECEDING May 25 — i.e. the Monday on or before May 24.
  // Anchoring on the 25th returned the 25th itself whenever it fell on a
  // Monday, putting the holiday a week late (2026, 2037).
  const may24 = new Date(year, 4, 24);
  const daysSinceMonday = (may24.getDay() + 6) % 7;
  const victoriaDay = new Date(may24);
  victoriaDay.setDate(24 - daysSinceMonday);
  return victoriaDay;
}

function getCivicHoliday(year) {
  const aug1 = new Date(year, 7, 1);
  const dayOfWeek = aug1.getDay();
  const daysToFirstMonday = (8 - dayOfWeek) % 7;
  const civicHoliday = new Date(aug1);
  civicHoliday.setDate(1 + daysToFirstMonday);
  return civicHoliday;
}

function getLabourDay(year) {
  const sept1 = new Date(year, 8, 1);
  const dayOfWeek = sept1.getDay();
  const daysToFirstMonday = (8 - dayOfWeek) % 7;
  const labourDay = new Date(sept1);
  labourDay.setDate(1 + daysToFirstMonday);
  return labourDay;
}

function getThanksgiving(year) {
  const oct1 = new Date(year, 9, 1);
  const dayOfWeek = oct1.getDay();
  const daysToFirstMonday = (8 - dayOfWeek) % 7;
  const thanksgiving = new Date(oct1);
  thanksgiving.setDate(1 + daysToFirstMonday + 7);
  return thanksgiving;
}

function getFamilyDay(year) {
  const feb1 = new Date(year, 1, 1);
  const dayOfWeek = feb1.getDay();
  const daysToFirstMonday = (8 - dayOfWeek) % 7;
  const familyDay = new Date(feb1);
  familyDay.setDate(1 + daysToFirstMonday + 14);
  return familyDay;
}

function isHoliday(date) {
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();

  const fixedHolidays = [
    { month: 1, day: 1 },
    { month: 7, day: 1 },
    { month: 11, day: 11 },
    { month: 12, day: 25 },
    { month: 12, day: 26 }
  ];

  for (const holiday of fixedHolidays) {
    if (month === holiday.month && day === holiday.day) return true;
  }

  const calculatedHolidays = [
    getGoodFriday(year),
    getVictoriaDay(year),
    getCivicHoliday(year),
    getLabourDay(year),
    getThanksgiving(year),
    getFamilyDay(year)
  ];

  for (const holiday of calculatedHolidays) {
    if (date.toDateString() === holiday.toDateString()) return true;
  }

  return false;
}

function isWeekend(date) {
  const day = date.getDay();
  return day === 0 || day === 6;
}

// ============================================================================
// TIME SELECTION UTILITIES
// ============================================================================

function getTimeWindows(date, isPickup = false) {
  const weekend = isWeekend(date) || isHoliday(date);

  if (isPickup) {
    return {
      AM: weekend ? TIME_CONFIG.pickupWeekendAM : TIME_CONFIG.pickupWeekdayAM,
      PM: weekend ? TIME_CONFIG.pickupWeekendPM : TIME_CONFIG.pickupWeekdayPM
    };
  } else {
    return {
      AM: weekend ? TIME_CONFIG.weekendAM : TIME_CONFIG.weekdayAM,
      PM: weekend ? TIME_CONFIG.weekendPM : TIME_CONFIG.weekdayPM
    };
  }
}

function getAvailablePeriods(date, isPickup = false) {
  const month = date.getMonth() + 1;
  const day = date.getDate();

  if (month === 12 && day === 24) {
    return { AM: true, PM: false };
  }

  if ((month === 12 && (day === 25 || day === 26)) || (month === 1 && day === 1)) {
    return { AM: false, PM: false };
  }

  return { AM: true, PM: true };
}

function getDayName(date) {
  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  return days[date.getDay()];
}

// ============================================================================
// DATE UTILITY FUNCTIONS
// ============================================================================

function getCurrentMonth(doc) {
  log('getCurrentMonth() called');

  const caption = doc.querySelector('caption, [class*="caption"], .rdp-caption_label');
  if (!caption) {
    log('❌ No caption element found');
    return null;
  }

  log('✓ Found caption element');
  const text = caption.textContent.trim();
  log('  Caption text:', text);

  // Extract just "Month Year" from the beginning
  const match = text.match(/^([A-Za-z]+)\s+(\d{4})/);
  if (!match) {
    log('❌ Could not extract month/year from caption');
    return null;
  }

  const result = {
    month: match[1],
    year: match[2]
  };

  log('✓ Parsed current month:', result);
  return result;
}

function getMonthIndex(monthName) {
  const months = ['January', 'February', 'March', 'April', 'May', 'June',
                 'July', 'August', 'September', 'October', 'November', 'December'];
  return months.indexOf(monthName);
}

function getMonthName(monthIndex) {
  const months = ['January', 'February', 'March', 'April', 'May', 'June',
                 'July', 'August', 'September', 'October', 'November', 'December'];
  return months[monthIndex];
}

function parseDateFromButton(button, doc) {
  log('parseDateFromButton() called');
  log('  Button element:', button);
  log('  Button text:', button.textContent);

  const td = button.closest('td');
  if (!td) {
    log('❌ Button is not inside a <td> element');
    return null;
  }

  const dateText = button.textContent.trim();
  log('  Date text:', dateText);

  const dateNumber = parseInt(dateText, 10);
  if (isNaN(dateNumber)) {
    log('❌ Could not parse date number from text');
    return null;
  }

  log('  Date number:', dateNumber);

  const tr = td.parentElement;
  const tbody = tr.parentElement;
  const clickedRow = Array.from(tbody.children).indexOf(tr);
  const totalRows = tbody.children.length;

  log('  Row position:', clickedRow, '/', totalRows);

  const currentMonth = getCurrentMonth(doc);
  if (!currentMonth) {
    log('❌ Could not get current month');
    return null;
  }

  const monthIndex = getMonthIndex(currentMonth.month);
  if (monthIndex === -1) {
    log('❌ Invalid month name');
    return null;
  }

  const year = parseInt(currentMonth.year, 10);

  let month = monthIndex;
  let resultYear = year;

  if (clickedRow === 0 && dateNumber > 20) {
    log('  Detected: Previous month date');
    month = monthIndex === 0 ? 11 : monthIndex - 1;
    resultYear = monthIndex === 0 ? year - 1 : year;
  } else if ((clickedRow === totalRows - 1 || clickedRow === totalRows - 2) && dateNumber <= 14) {
    log('  Detected: Next month date');
    month = (monthIndex + 1) % 12;
    resultYear = monthIndex === 11 ? year + 1 : year;
  } else {
    log('  Detected: Current month date');
  }

  const result = {
    day: dateNumber,
    month: getMonthName(month),
    monthIndex: month,
    year: resultYear,
    button: button
  };

  log('✓ Parsed date:', result);
  return result;
}

function formatDateShort(dateObj) {
  const day = dateObj.day;
  const ordinal = getOrdinal(day);
  return `${dateObj.month} ${day}${ordinal}`;
}

function getOrdinal(day) {
  if (day > 3 && day < 21) return 'th';
  switch (day % 10) {
    case 1: return 'st';
    case 2: return 'nd';
    case 3: return 'rd';
    default: return 'th';
  }
}

function formatAlternativeDate(dateObj) {
  const months = ['January', 'February', 'March', 'April', 'May', 'June',
                 'July', 'August', 'September', 'October', 'November', 'December'];
  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

  const date = new Date(dateObj.year, dateObj.monthIndex, dateObj.day);
  const dayName = days[date.getDay()];
  const monthName = months[dateObj.monthIndex];

  return `${dayName}, ${monthName} ${dateObj.day}`;
}

function isRestrictedDate(dateObj) {
  log('isRestrictedDate() called');
  log('  Checking date:', dateObj);

  const month = dateObj.monthIndex + 1;
  const day = dateObj.day;

  log(`  Date to check: Month ${month}, Day ${day}`);

  for (const key in CONFIG.restrictions) {
    const restriction = CONFIG.restrictions[key];
    log(`  Comparing against ${key}: Month ${restriction.month}, Day ${restriction.day}`);

    if (restriction.month === month && restriction.day === day) {
      log(`✓ Restriction found: ${key} (${restriction.type})`);
      return restriction;
    }
  }

  log('✗ Not a restricted date');
  return null;
}

function findAlternativeDates(restrictedDate) {
  log('findAlternativeDates() called for:', formatDateShort(restrictedDate));

  const findNextAvailable = (startDate, direction = 'forward') => {
    let testDate = new Date(startDate.year, startDate.monthIndex, startDate.day);
    let maxIterations = 10;
    let iterations = 0;

    while (iterations < maxIterations) {
      if (direction === 'forward') {
        testDate.setDate(testDate.getDate() + 1);
      } else {
        testDate.setDate(testDate.getDate() - 1);
      }

      iterations++;

      const month = testDate.getMonth() + 1;
      const day = testDate.getDate();

      let isRestricted = false;
      for (const key in CONFIG.restrictions) {
        const restriction = CONFIG.restrictions[key];
        if (restriction.month === month && restriction.day === day) {
          isRestricted = true;
          break;
        }
      }

      if (!isRestricted) {
        return {
          day: testDate.getDate(),
          month: getMonthName(testDate.getMonth()),
          monthIndex: testDate.getMonth(),
          year: testDate.getFullYear()
        };
      }
    }

    return null;
  };

  const alternatives = {
    before: findNextAvailable(restrictedDate, 'backward'),
    after: findNextAvailable(restrictedDate, 'forward')
  };

  log('  Alternative dates found:', alternatives);
  return alternatives;
}

// ============================================================================
// FLEX BOOKING — PRICE READER & MODAL
// ============================================================================

/**
 * Reads the booking price from the BTA iframe price element.
 * Returns the amount as a float, or null if not found.
 */
function getBookingAmountFromIframe(doc) {
  if (!doc) return null;

  let priceElement = findPriceElement(doc);
  if (!priceElement) {
    const allSpans = doc.querySelectorAll('span');
    for (const span of allSpans) {
      if (span.textContent.includes('$') && span.textContent.match(/\d/)) {
        const prevText = span.previousSibling?.textContent || span.parentElement?.textContent || '';
        if (prevText.toLowerCase().includes('price')) {
          priceElement = span;
          break;
        }
      }
    }
  }

  if (!priceElement) {
    log('⚠ getBookingAmountFromIframe: could not find price element');
    return null;
  }

  const raw = priceElement.textContent.trim().replace(/[^0-9.]/g, '');
  const amount = parseFloat(raw);
  if (isNaN(amount) || amount <= 0) {
    log('⚠ getBookingAmountFromIframe: could not parse price:', priceElement.textContent);
    return null;
  }

  log(`✓ getBookingAmountFromIframe: $${amount}`);
  return amount;
}

function showFlexModal(bookingAmount) {
  return new Promise((resolve) => {
    log('Showing Flex Booking modal');

    // ── Fee calculations ─────────────────────────────────────────────────────
    const flexFee  = bookingAmount ? FLEX_CONFIG.calculateFee(bookingAmount) : null;
    // Refund = full booking amount (Flex fee is a separate add-on, not deducted from booking)
    const refundBack = bookingAmount || null;
    const fmt      = (n) => n !== null ? '$' + n.toFixed(2) : '—';
    const feeLabel = flexFee !== null ? fmt(flexFee) : '';

    const modalId = 'flex-booking-modal-' + Date.now();

    // ── v4.7: stay summary so the customer sees exactly what is booked ──────
    let staySummaryHTML = '';
    try {
      const dO = timeState.dropOffDate;
      const pO = timeState.pickUpDate;
      if (dO && pO) {
        const toDate = (o) => new Date(o.year, o.monthIndex, o.day);
        const label = (o) => `${getDayName(toDate(o))}, ${getMonthName(o.monthIndex)} ${o.day}`;
        const period = (p) => p === 'AM' ? 'morning' : (p === 'PM' ? 'afternoon' : '');
        const nights = Math.round((Date.UTC(pO.year, pO.monthIndex, pO.day) -
                                   Date.UTC(dO.year, dO.monthIndex, dO.day)) / 86400000);
        const nightsLabel = nights === 1 ? '1 night' : `${nights} nights`;
        staySummaryHTML = `
          <div style="padding:14px 24px 0;">
            <div style="background:#fdfaf5;border:1px solid #e8d9bb;border-radius:8px;padding:12px 14px;font-size:14px;color:#374151;line-height:1.6;">
              <div style="font-weight:700;color:#1f2937;margin-bottom:4px;">Your stay</div>
              <div><strong>Drop-off:</strong> ${label(dO)}${period(timeState.dropOffTime) ? ' (' + period(timeState.dropOffTime) + ')' : ''}</div>
              <div><strong>Pick-up:</strong> ${label(pO)}${period(timeState.pickUpTime) ? ' (' + period(timeState.pickUpTime) + ')' : ''}</div>
              <div style="margin-top:4px;color:#6b7280;">${nightsLabel}${bookingAmount ? ' &middot; booking total ' + fmt(bookingAmount) : ''}</div>
              <button type="button" id="kcb-flex-startover" style="
                background:none;border:none;padding:0;margin-top:8px;
                color:#a37f4a;font-size:13px;font-weight:600;text-decoration:underline;cursor:pointer;
              ">These aren't my dates &ndash; start over</button>
            </div>
          </div>`;
      }
    } catch (sumErr) { staySummaryHTML = ''; }

    // ── Cancellation breakdown (always shown when numbers available) ─────────
    const totalPaid = bookingAmount && flexFee !== null ? bookingAmount + flexFee : null;
    const breakdownHTML = (bookingAmount && flexFee !== null) ? `
      <div style="
        background:#f9fafb;border:1px solid #e5e7eb;border-radius:6px;
        padding:11px 14px;margin:10px 0 14px;font-size:13px;
      ">
        <div style="font-weight:600;color:#374151;margin-bottom:7px;">If you cancel:</div>
        <div style="display:flex;justify-content:space-between;color:#6b7280;margin-bottom:3px;">
          <span>Total paid (booking + protection)</span><span>${fmt(totalPaid)}</span>
        </div>
        <div style="display:flex;justify-content:space-between;color:#6b7280;margin-bottom:3px;">
          <span>Protection cost (non-refundable)</span><span>− ${fmt(flexFee)}</span>
        </div>
        <div style="border-top:1px solid #e5e7eb;margin:7px 0;"></div>
        <div style="display:flex;justify-content:space-between;font-weight:700;color:#15803d;">
          <span>You receive back</span><span>${fmt(bookingAmount)}</span>
        </div>
      </div>` : '';

    // ── Modal HTML ───────────────────────────────────────────────────────────
    const modalHTML = `
      <style>
        @keyframes kcbFlexFadeIn  { from{opacity:0} to{opacity:1} }
        @keyframes kcbFlexSlideUp { from{transform:translateY(30px);opacity:0} to{transform:translateY(0);opacity:1} }
        @keyframes kcbFlexFadeOut { from{opacity:1} to{opacity:0} }

        .kcb-flex-option {
          display:flex;align-items:flex-start;gap:13px;
          border:2px solid #e5e7eb;border-radius:10px;
          padding:14px 16px;cursor:pointer;
          transition:border-color 0.18s,background 0.18s;
          background:white;margin-bottom:10px;
        }
        .kcb-flex-option:last-of-type { margin-bottom:0; }
        .kcb-flex-option.selected-yes {
          border-color:#BD9B60;background:#fdfaf5;
        }
        .kcb-flex-option.selected-no {
          border-color:#d1d5db;background:#f9fafb;
        }
        .kcb-flex-radio {
          width:20px;height:20px;border-radius:50%;
          border:2px solid #d1d5db;flex-shrink:0;margin-top:2px;
          display:flex;align-items:center;justify-content:center;
          transition:border-color 0.18s,background 0.18s;
        }
        .kcb-flex-option.selected-yes .kcb-flex-radio {
          border-color:#BD9B60;background:#BD9B60;
        }
        .kcb-flex-option.selected-yes .kcb-flex-radio::after {
          content:'';display:block;width:8px;height:8px;
          border-radius:50%;background:white;
        }
        .kcb-flex-option.selected-no .kcb-flex-radio {
          border-color:#9ca3af;background:#9ca3af;
        }
        .kcb-flex-option.selected-no .kcb-flex-radio::after {
          content:'';display:block;width:8px;height:8px;
          border-radius:50%;background:white;
        }
        #kcb-flex-confirm-btn {
          width:100%;padding:14px;border:none;border-radius:8px;
          background:#BD9B60;color:white;font-size:15px;font-weight:700;
          cursor:pointer;transition:background 0.18s;letter-spacing:0.01em;
          margin-top:18px;
        }
        #kcb-flex-confirm-btn:hover { background:#a37f4a; }
        #kcb-flex-confirm-btn:disabled { background:#d1c4a8;cursor:not-allowed; }
        #kcb-flex-learn-btn {
          background:none;border:none;padding:0;
          color:#BD9B60;font-size:13px;font-weight:600;
          cursor:pointer;text-decoration:underline;
          display:inline-flex;align-items:center;gap:4px;
        }
        @media(max-width:480px){
          #${modalId} .kcb-flex-inner {
            margin:0 auto !important;width:100% !important;
            /* v4.7.1: 94vh cap removed - height is now fitted to the visible screen in JS */
          }
        }
      </style>

      <div id="${modalId}" style="
        position:fixed;inset:0;z-index:9999999;
        background:rgba(0,0,0,0.6);
        display:flex;align-items:flex-start;justify-content:center;
        padding:16px 12px;box-sizing:border-box;
        animation:kcbFlexFadeIn 0.25s ease-out;
        overflow-y:auto;
      ">
        <div class="kcb-flex-inner" style="
          background:white;border-radius:14px;
          width:100%;max-width:480px;
          box-shadow:0 20px 60px rgba(0,0,0,0.25);
          font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;
          animation:kcbFlexSlideUp 0.28s ease-out;
          display:flex;flex-direction:column;
          max-height:calc(100vh - 32px);max-height:calc(100dvh - 32px);
          overflow-x:hidden;overflow-y:auto;
        ">

          <!-- Top accent bar -->
          <div style="height:5px;background:#BD9B60;flex:0 0 auto;"></div>
          <!-- v4.7.1: scrolling middle - everything except the pinned footer -->
          <div class="kcb-flex-scroll" style="flex:1 1 auto;min-height:0;overflow-y:auto;-webkit-overflow-scrolling:touch;overscroll-behavior:contain;">

          <!-- Header -->
          <div style="padding:22px 24px 0;">
            <div style="display:flex;align-items:center;gap:12px;margin-bottom:6px;">
              <div style="
                width:38px;height:38px;border-radius:50%;
                background:#fdf6e9;border:2px solid #BD9B60;
                display:flex;align-items:center;justify-content:center;
                font-size:18px;flex-shrink:0;
              ">🛡️</div>
              <div>
                <div style="font-size:18px;font-weight:700;color:#1f2937;line-height:1.2;">
                  Booking Protection
                </div>
                <div style="font-size:13px;color:#6b7280;margin-top:2px;">
                  Life is unpredictable. Flex Booking keeps your plans protected.
                </div>
              </div>
            </div>
          </div>
          ${staySummaryHTML}

          <!-- Divider -->
          <div style="height:1px;background:#f3f4f6;margin:16px 0 0;"></div>

          <!-- Options -->
          <div style="padding:16px 24px 0;">

            <!-- Option: Yes -->
            <div class="kcb-flex-option selected-yes" id="kcb-opt-yes">
              <div class="kcb-flex-radio"></div>
              <div style="flex:1;">
                <div style="display:flex;justify-content:space-between;align-items:center;flex-wrap:wrap;gap:6px;">
                  <div style="font-size:15px;font-weight:700;color:#1f2937;">
                    Protect my booking
                  </div>
                  <div style="
                    background:#fdf6e9;border:1px solid #BD9B60;
                    color:#92400e;font-size:13px;font-weight:700;
                    padding:3px 10px;border-radius:20px;white-space:nowrap;
                  ">${feeLabel}</div>
                </div>
                <div style="font-size:13px;color:#15803d;font-weight:600;margin-top:4px;">
                  ✓ Refundable &amp; Transferable
                </div>
                <div style="font-size:12.5px;color:#6b7280;margin-top:3px;line-height:1.5;">
                  Cancel or transfer up to 6pm the day before your stay begins — fully refundable &amp; transferable.
                </div>
              </div>
            </div>

            <!-- Option: No -->
            <div class="kcb-flex-option" id="kcb-opt-no">
              <div class="kcb-flex-radio"></div>
              <div style="flex:1;">
                <div style="font-size:15px;font-weight:700;color:#374151;">
                  No protection
                </div>
                <div style="font-size:13px;color:#dc2626;font-weight:600;margin-top:4px;">
                  ✕ Non-Refundable &amp; Non-Transferable
                </div>
                <div style="font-size:12.5px;color:#6b7280;margin-top:3px;line-height:1.5;">
                  I accept this booking is final and cannot be cancelled or transferred.
                </div>
              </div>
            </div>


            <!-- Learn more toggle -->
            <div style="margin-top:14px;">
              <button id="kcb-flex-learn-btn">▸ Conditions</button>
            </div>

            <!-- Learn more content -->
            <div id="kcb-flex-learn-content" style="display:none;margin-top:12px;">
              <div style="
                background:#f9fafb;border:1px solid #e5e7eb;border-radius:8px;
                padding:16px;font-size:13px;color:#374151;line-height:1.65;
              ">
                <div style="font-weight:700;color:#1f2937;margin-bottom:5px;">Cancellations</div>
                <p style="margin:0 0 8px;">
                  Submit a cancellation request by 6pm the day before your stay begins and receive a full refund of your <strong>booking amount</strong>. The cost of Flex Protection is non-refundable.
                </p>
                ${breakdownHTML}
                <p style="margin:0 0 14px;color:#6b7280;">
                  Cancellations must be requested by 6pm the day before your stay begins. Requests received after 6pm — or on, or after, the day your stay begins — cannot be cancelled or transferred, and the booking will be charged in full. A boarding stay is treated as a single booking: once it has begun, the full stay is charged, including any days you do not use. Collecting your dog early does not result in a refund. Before the deadline you may also shorten a stay, with any days removed refunded.
                </p>

                <div style="font-weight:700;color:#1f2937;margin-bottom:5px;">Transfers</div>
                <p style="margin:0 0 14px;">
                  Transfer requests must be submitted by 6pm the day before your stay begins. Requests received after 6pm — or on, or after, the day your stay begins — cannot be transferred or cancelled, and the booking will be charged in full. A boarding stay transfers as a whole: once it has begun, no part of it can be transferred, including days later in the stay that have not yet started. Transfers requested prior to the deadline are subject to availability and carry no additional charge.
                </p>

                <div style="font-weight:700;color:#1f2937;margin-bottom:5px;">Please note</div>
                <p style="margin:0 0 8px;color:#6b7280;">
                  No-show bookings are not eligible for a refund or transfer regardless of Flex Booking Protection status.
                </p>
                <p style="margin:0 0 8px;color:#6b7280;">
                  The cost of Flex Booking Protection is non-refundable in all cases.
                  All refund and transfer requests must be submitted by email to
                  <a href="mailto:info@kennelclubboarding.ca"
                     style="color:#BD9B60;font-weight:600;">
                    info@kennelclubboarding.ca
                  </a>
                </p>
                <p style="margin:0;color:#6b7280;">
                  <a href="https://kennelclubboarding.ca/policies/refund-policy"
                     target="_blank"
                     style="color:#BD9B60;font-weight:600;">
                    View full Cancellation &amp; Refund Policy →
                  </a>
                </p>
              </div>
            </div>

          </div>

          <!-- Confirm button -->
          </div><!-- /kcb-flex-scroll -->
          <!-- v4.7.1: pinned footer - booking total + Confirm always on screen -->
          <div class="kcb-flex-footer" style="flex:0 0 auto;padding:12px 24px 16px;background:white;border-top:1px solid #f3f4f6;box-shadow:0 -6px 14px rgba(0,0,0,0.05);">
            ${bookingAmount && flexFee !== null ? `
            <!-- Booking cost summary — shown after options for context -->
            <div style="
              display:flex;justify-content:space-between;align-items:center;
              padding:0 0 10px;margin-top:0;
              border-top:none;
            ">
              <span style="font-size:13px;color:#374151;">Booking total</span>
              <div id="kcb-flex-total-display" style="font-size:13px;font-weight:700;color:#1f2937;">${fmt(bookingAmount + flexFee)}</div>
            </div>
            ` : ''}
            <button id="kcb-flex-confirm-btn">Confirm</button>
          </div>

        </div><!-- /inner -->
      </div><!-- /overlay -->
    `;

    // ── Inject ───────────────────────────────────────────────────────────────
    const container = document.createElement('div');
    container.innerHTML = modalHTML;
    document.body.appendChild(container);
    document.body.style.overflow = 'hidden';

    // v4.7.1: size the card to the visible screen (visualViewport excludes the
    // browser toolbars) so the pinned Confirm footer is always on screen
    const kcbFlexFit = () => {
      try {
        const fitInner = container.querySelector('.kcb-flex-inner');
        if (!fitInner || !fitInner.isConnected) {
          if (window.visualViewport) window.visualViewport.removeEventListener('resize', kcbFlexFit);
          window.removeEventListener('resize', kcbFlexFit);
          return;
        }
        const visibleH = (window.visualViewport && window.visualViewport.height) || window.innerHeight;
        fitInner.style.maxHeight = Math.max(300, Math.floor(visibleH - 32)) + 'px';
      } catch (fitErr) { /* never block the modal */ }
    };
    kcbFlexFit();
    if (window.visualViewport) window.visualViewport.addEventListener('resize', kcbFlexFit);
    window.addEventListener('resize', kcbFlexFit);

    // ── State ────────────────────────────────────────────────────────────────
    let currentDecision = 'yes'; // default pre-selected

    const optYes = document.getElementById('kcb-opt-yes');
    const optNo  = document.getElementById('kcb-opt-no');

    const setSelection = (decision) => {
      currentDecision = decision;
      if (decision === 'yes') {
        optYes.classList.add('selected-yes');
        optYes.classList.remove('selected-no');
        optNo.classList.remove('selected-yes', 'selected-no');
      } else {
        optNo.classList.add('selected-no');
        optNo.classList.remove('selected-yes');
        optYes.classList.remove('selected-yes', 'selected-no');
      }
      const totalDisplay = document.getElementById('kcb-flex-total-display');
      if (totalDisplay && flexFee !== null) {
        totalDisplay.textContent = decision === 'yes' ? fmt(bookingAmount + flexFee) : fmt(bookingAmount);
      }
    };

    optYes.addEventListener('click', () => setSelection('yes'));
    optNo.addEventListener('click',  () => setSelection('no'));

    // v4.7: "These aren't my dates - start over" -> clean calendar
    const startOverLink = document.getElementById('kcb-flex-startover');
    if (startOverLink) {
      startOverLink.addEventListener('click', (ev) => {
        ev.stopPropagation();
        telNotable('flex_startover', {
          dropOff: kcbDateKey(timeState.dropOffDate),
          pickUp: kcbDateKey(timeState.pickUpDate),
          bookingAmount: bookingAmount || null
        });
        telSend('flex_startover');
        resetBookingSelection();
      });
    }

    // ── Learn more toggle ────────────────────────────────────────────────────
    const learnBtn     = document.getElementById('kcb-flex-learn-btn');
    const learnContent = document.getElementById('kcb-flex-learn-content');
    learnBtn.addEventListener('click', () => {
      const open = learnContent.style.display !== 'none';
      learnContent.style.display = open ? 'none' : 'block';
      learnBtn.innerHTML = open
        ? '▸ Conditions'
        : '▾ Hide conditions';
    });

    // ── Confirm ──────────────────────────────────────────────────────────────
    const closeModal = (decision) => {
      const overlay = document.getElementById(modalId);
      if (overlay) overlay.style.animation = 'kcbFlexFadeOut 0.2s ease-out';
      setTimeout(() => {
        container.remove();
        document.body.style.overflow = 'auto';
        log(`✓ Flex modal confirmed — decision: ${decision}`);
        // Small additional delay ensures modal is fully gone from DOM
        // before waitForRadiosAndSelect() fires, preventing click bleed-through
        setTimeout(() => resolve(decision), 50);
      }, 200);
    };

    // Main screen content wrapper — used to swap in second confirmation
    const mainContent = container.querySelector('.kcb-flex-main-content');

    const showSecondConfirmation = () => {
      // Swap modal body to second confirmation screen
      const inner = container.querySelector('.kcb-flex-inner');
      if (!inner) { closeModal('no'); return; }

      inner.innerHTML = `
        <div style="height:5px;background:#e5e7eb;border-radius:14px 14px 0 0;"></div>
        <div style="padding:28px 24px 24px;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;">

          <!-- Icon + headline -->
          <div style="text-align:center;margin-bottom:20px;">
            <div style="font-size:36px;margin-bottom:10px;">⚠️</div>
            <div style="font-size:17px;font-weight:700;color:#1f2937;margin-bottom:8px;">
              Just to confirm
            </div>
            <div style="
              font-size:14px;color:#374151;line-height:1.6;
              background:#fef2f2;border:1px solid #fecaca;
              border-radius:8px;padding:14px 16px;text-align:left;
            ">
              This booking will be <strong>non-refundable and non-transferable</strong>.
              No cancellations, refunds, or date changes will be possible after checkout.
            </div>
          </div>

          <!-- Buttons -->
          <div style="display:flex;flex-direction:column;gap:10px;margin-top:4px;">
            <button id="kcb-flex-goback-btn" style="
              width:100%;padding:14px;border-radius:8px;
              border:none;background:#BD9B60;
              color:white;font-size:15px;font-weight:700;
              cursor:pointer;transition:background 0.18s;
            ">Yes — protect my booking for ${feeLabel}</button>
            <button id="kcb-flex-confirm2-btn" style="
              width:100%;padding:12px;border-radius:8px;
              border:1.5px solid #d1d5db;background:white;
              color:#6b7280;font-size:13px;font-weight:500;
              cursor:pointer;transition:all 0.18s;line-height:1.3;
            ">No thanks — I understand my booking is non-refundable and non-transferable</button>
          </div>

        </div>
      `;

      // Protect my booking — resolve yes directly, no need to rebuild modal
      document.getElementById('kcb-flex-goback-btn').addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        closeModal('yes');
      });

      // Confirm no protection
      document.getElementById('kcb-flex-confirm2-btn').addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        closeModal('no');
      });
    };

    document.getElementById('kcb-flex-confirm-btn').addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation();
      if (currentDecision === 'no') {
        showSecondConfirmation();
      } else {
        closeModal('yes');
      }
    });
  });
}

// ============================================================================
// TIME SELECTION MODAL
// ============================================================================

function generateTimeSelectionModal(date, isPickup, dateObj) {
  const modalId = 'time-selection-modal-' + Date.now();
  const dateType = isPickup ? 'pick-up' : 'drop-off';
  const timeWindows = getTimeWindows(date, isPickup);
  const available = getAvailablePeriods(date, isPickup);

  const dayName = getDayName(date);
  const dateStr = formatDateShort(dateObj);

  const modalHTML = `
    <div id="${modalId}" class="time-selection-modal" style="
      display: block;
      position: fixed;
      z-index: 999999;
      left: 0;
      top: 0;
      width: 100%;
      height: 100%;
      background-color: rgba(0, 0, 0, 0.6);
      overflow: auto;
      animation: fadeIn 0.3s ease-out;
    ">
      <div class="modal-content" style="
        background-color: white;
        margin: 10% auto;
        padding: 0;
        border: 3px solid #BD9B60;
        border-radius: 12px;
        width: 90%;
        max-width: 480px;
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
        position: relative;
        animation: slideDown 0.3s ease-out;
      ">
        <div style="
          background: linear-gradient(135deg, #BD9B60 0%, #a37f4a 100%);
          padding: 18px 24px;
          border-radius: 9px 9px 0 0;
          position: relative;
        ">
          <div style="font-size: 20px; font-weight: 700; color: white;">
            Select ${dateType} time
          </div>
          <div style="font-size: 14px; color: rgba(255, 255, 255, 0.9); margin-top: 4px;">
            ${dayName}, ${dateStr}
          </div>
        </div>

        <div style="padding: 24px;">
          <div style="
            background: #f9fafb;
            border: 2px solid #e5e7eb;
            border-radius: 8px;
            padding: 16px;
            margin-bottom: 20px;
          ">
            <div style="font-size: 15px; color: #374151; line-height: 1.6; margin-bottom: 16px;">
              Please select your ${dateType} time window. To keep all our furry guests safe, our facility is locked at all other times — please plan to arrive within this window.
            </div>

            <div class="time-option ${available.AM ? '' : 'disabled'}" data-period="AM" style="
              background: ${available.AM ? 'white' : '#f3f4f6'};
              border: 2px solid ${available.AM ? '#d1d5db' : '#e5e7eb'};
              border-radius: 8px;
              padding: 16px;
              margin-bottom: 12px;
              cursor: ${available.AM ? 'pointer' : 'not-allowed'};
              transition: all 0.2s;
              opacity: ${available.AM ? '1' : '0.5'};
            ">
              <div style="display: flex; justify-content: space-between; align-items: center;">
                <div>
                  <div style="font-size: 16px; font-weight: 600; color: ${available.AM ? '#1f2937' : '#9ca3af'}; margin-bottom: 4px;">
                    Morning (AM)
                  </div>
                  <div style="font-size: 14px; color: ${available.AM ? '#6b7280' : '#9ca3af'};">
                    ${timeWindows.AM.start} - ${timeWindows.AM.end}
                  </div>
                </div>
                ${available.AM ? `
                  <div style="
                    width: 24px;
                    height: 24px;
                    border: 2px solid #d1d5db;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                  " class="radio-indicator">
                  </div>
                ` : `
                  <div style="font-size: 12px; color: #9ca3af; font-weight: 600;">
                    Not Available
                  </div>
                `}
              </div>
            </div>

            <div class="time-option ${available.PM ? '' : 'disabled'}" data-period="PM" style="
              background: ${available.PM ? 'white' : '#f3f4f6'};
              border: 2px solid ${available.PM ? '#d1d5db' : '#e5e7eb'};
              border-radius: 8px;
              padding: 16px;
              cursor: ${available.PM ? 'pointer' : 'not-allowed'};
              transition: all 0.2s;
              opacity: ${available.PM ? '1' : '0.5'};
            ">
              <div style="display: flex; justify-content: space-between; align-items: center;">
                <div>
                  <div style="font-size: 16px; font-weight: 600; color: ${available.PM ? '#1f2937' : '#9ca3af'}; margin-bottom: 4px;">
                    Afternoon (PM)
                  </div>
                  <div style="font-size: 14px; color: ${available.PM ? '#6b7280' : '#9ca3af'};">
                    ${timeWindows.PM.start} - ${timeWindows.PM.end}
                  </div>
                </div>
                ${available.PM ? `
                  <div style="
                    width: 24px;
                    height: 24px;
                    border: 2px solid #d1d5db;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                  " class="radio-indicator">
                  </div>
                ` : `
                  <div style="font-size: 12px; color: #9ca3af; font-weight: 600;">
                    Not Available
                  </div>
                `}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <style>
      @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
      }

      @keyframes slideDown {
        from {
          transform: translateY(-50px);
          opacity: 0;
        }
        to {
          transform: translateY(0);
          opacity: 1;
        }
      }

      @keyframes fadeOut {
        from { opacity: 1; }
        to { opacity: 0; }
      }

      .time-option:not(.disabled):hover {
        border-color: #BD9B60 !important;
        box-shadow: 0 4px 12px rgba(189, 155, 96, 0.2);
        transform: translateY(-2px);
      }

      .time-option.selected {
        border-color: #BD9B60 !important;
        background: #fffbf5 !important;
      }

      .time-option.selected .radio-indicator {
        border-color: #BD9B60 !important;
        background: #BD9B60 !important;
      }

      .time-option.selected .radio-indicator::after {
        content: '✓';
        color: white;
        font-size: 14px;
        font-weight: bold;
      }

      /* Mobile responsiveness */
      @media (max-width: 480px) {
        .time-selection-modal .modal-content {
          margin: 5vh auto !important;
          width: 95% !important;
          max-height: 90vh;
          overflow-y: auto;
        }

        .time-option {
          padding: 18px !important;
        }
      }

      /* Ensure touch targets are large enough */
      @media (hover: none) and (pointer: coarse) {
        .time-option {
          min-height: 60px;
        }
      }
    </style>
  `;

  return {
    html: modalHTML,
    modalId: modalId,
    available: available
  };
}

function showTimeSelectionModal(dateObj, isPickup) {
  return new Promise((resolve) => {
    log(`═══════════════════════════════════════════════════════════`);
    log(`Showing time selection modal for ${isPickup ? 'PICK-UP' : 'DROP-OFF'}`);

    // v4.3 telemetry: what date did the customer actually SEE in this modal
    telEvent('timeModal', {
      isPickup: !!isPickup,
      displayedDate: `${dateObj.year}-${dateObj.monthIndex + 1}-${dateObj.day}`
    });

    // Convert custom date object to JavaScript Date object
    const date = new Date(dateObj.year, dateObj.monthIndex, dateObj.day);

    log(`Date: ${formatDateShort(dateObj)} (${getDayName(date)})`);

    const modalData = generateTimeSelectionModal(date, isPickup, dateObj);

    const modalContainer = document.createElement('div');
    modalContainer.innerHTML = modalData.html;
    document.body.appendChild(modalContainer);

    state.modalActive = true;

    const modal = document.getElementById(modalData.modalId);
    const timeOptions = modal.querySelectorAll('.time-option:not(.disabled)');

    timeOptions.forEach(option => {
      option.addEventListener('click', (e) => {
        e.stopPropagation();

        const period = option.getAttribute('data-period');
        log(`✓ User selected: ${period}`);

        timeOptions.forEach(opt => opt.classList.remove('selected'));
        option.classList.add('selected');

        if (isPickup) {
            timeState.pickUpTime = period;
            timeState.pickUpDate = dateObj; // Store the custom date object
            } else {
            timeState.dropOffTime = period;
            timeState.dropOffDate = dateObj; // Store the custom date object
            }

        setTimeout(() => {
          modal.style.animation = 'fadeOut 0.2s ease-out';
          setTimeout(() => {
            modalContainer.remove();
            state.modalActive = false;
            document.body.style.overflow = 'auto';
            log(`✓ Time selection modal closed`);
            log(`═══════════════════════════════════════════════════════════`);
            resolve(period);
          }, 200);
        }, 300);
      });
    });

    document.body.style.overflow = 'hidden';
  });
}

function resetTimeSelections() {
  log('Resetting time selections');
  timeState.dropOffTime = null;
  timeState.dropOffDate = null;
  timeState.pickUpTime = null;
  timeState.pickUpDate = null;
}

// ============================================================================
// SOLD OUT DETECTION & MODAL
// ============================================================================

function isNextDaySoldOut(dateObj, doc) {
  log('isNextDaySoldOut() - Checking if next day is sold out');

  // Calculate next day
  const nextDay = new Date(dateObj.year, dateObj.monthIndex, dateObj.day + 1);
  const nextDayNum = nextDay.getDate();
  const nextMonthIndex = nextDay.getMonth();
  const nextYear = nextDay.getFullYear();

  log(`  Current date: ${dateObj.month} ${dateObj.day}, ${dateObj.year}`);
  log(`  Next day: ${getMonthName(nextMonthIndex)} ${nextDayNum}, ${nextYear}`);

  // Get the current month being displayed in the calendar
  const currentMonth = getCurrentMonth(doc);
  if (!currentMonth) {
    log('❌ Could not determine current calendar month');
    return false; // Fail safe - don't block if we can't determine
  }

  const currentMonthIndex = getMonthIndex(currentMonth.month);
  log(`  Calendar showing: ${currentMonth.month} ${currentMonth.year}`);

  // Find all date buttons in the calendar
  const allButtons = doc.querySelectorAll('button.rdp-day');

  for (const btn of allButtons) {
    const btnText = parseInt(btn.textContent.trim(), 10);

    // Skip if not the right day number
    if (btnText !== nextDayNum) continue;

    // Parse this button to get its full date context
    const btnDateObj = parseDateFromButton(btn, doc);
    if (!btnDateObj) continue;

    log(`  Checking button: ${btnDateObj.month} ${btnDateObj.day}, ${btnDateObj.year}`);

    // Check if this button matches the next day we're looking for
    if (btnDateObj.year === nextYear && 
        btnDateObj.monthIndex === nextMonthIndex && 
        btnDateObj.day === nextDayNum) {

      // This is the correct next day button - check if it's disabled
      const isDisabled = btn.hasAttribute('disabled') || 
                         btn.classList.contains('rdp-day_disabled') ||
                         btn.classList.contains('disabled');

      log(`  ✓ Found correct next day button: ${getMonthName(nextMonthIndex)} ${nextDayNum}, ${nextYear}`);
      log(`    Disabled: ${isDisabled}`);

      if (isDisabled) {
        log('✓ Next day is SOLD OUT');
        return true;
      } else {
        log('✓ Next day is AVAILABLE');
        return false;
      }
    }
  }

  log('⚠ Could not find next day button in calendar');
  return false; // Fail safe - don't block if we can't find the button
}

// ============================================================================
// SOLD-OUT CASCADE LOGIC
// ============================================================================

/**
 * Check if a button represents a sold-out date (not just a past date)
 * Sold-out dates are disabled but are in the future
 */
function isButtonSoldOut(button, doc) {
  // Must be disabled first
  const isDisabled = button.hasAttribute('disabled') || 
                     button.classList.contains('rdp-day_disabled') ||
                     button.classList.contains('disabled');

  if (!isDisabled) return false;

  // Parse the date from the button
  const dateObj = parseDateFromButton(button, doc);
  if (!dateObj) return false;

  // Create a Date object for comparison
  const buttonDate = new Date(dateObj.year, dateObj.monthIndex, dateObj.day);
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  // If the date is in the past, it's not "sold out" - it's just past
  if (buttonDate < today) return false;

  // If it's today or future and disabled, it's sold out
  return true;
}

/**
 * Find all sold-out dates after a given drop-off date
 * Returns array of date objects sorted by date
 */
function findSoldOutDatesAfterDropOff(dropOffDate, doc) {
  log('findSoldOutDatesAfterDropOff() called');
  log('  Drop-off date:', formatDateShort(dropOffDate));

  const soldOutDates = [];
  const dropOff = new Date(dropOffDate.year, dropOffDate.monthIndex, dropOffDate.day);

  // Get all date buttons in the calendar
  const allButtons = doc.querySelectorAll('button.rdp-day');

  for (const btn of allButtons) {
    // Check if this button is sold out
    if (isButtonSoldOut(btn, doc)) {
      const dateObj = parseDateFromButton(btn, doc);
      if (dateObj) {
        const btnDate = new Date(dateObj.year, dateObj.monthIndex, dateObj.day);

        // Only include dates AFTER the drop-off date
        if (btnDate > dropOff) {
          soldOutDates.push({
            day: dateObj.day,
            month: dateObj.month,
            monthIndex: dateObj.monthIndex,
            year: dateObj.year
          });
          log('  Found sold-out date: ' + formatDateShort(dateObj));
        }
      }
    }
  }

  // Sort by date
  soldOutDates.sort(function(a, b) {
    const dateA = new Date(a.year, a.monthIndex, a.day);
    const dateB = new Date(b.year, b.monthIndex, b.day);
    return dateA - dateB;
  });

  log('  Total sold-out dates found after drop-off: ' + soldOutDates.length);
  return soldOutDates;
}

/**
 * Find sold-out dates between two dates (for the modal message)
 */
function findSoldOutDatesBetween(startDate, endDate, doc) {
  log('findSoldOutDatesBetween() called');

  const soldOutDates = [];
  const start = new Date(startDate.year, startDate.monthIndex, startDate.day);
  const end = new Date(endDate.year, endDate.monthIndex, endDate.day);

  // Check our cached sold-out dates
  for (var i = 0; i < state.soldOutDatesInRange.length; i++) {
    var soldOut = state.soldOutDatesInRange[i];
    var soldOutDate = new Date(soldOut.year, soldOut.monthIndex, soldOut.day);
    if (soldOutDate > start && soldOutDate <= end) {
      soldOutDates.push(soldOut);
    }
  }

  // Sort by date
  soldOutDates.sort(function(a, b) {
    var dateA = new Date(a.year, a.monthIndex, a.day);
    var dateB = new Date(b.year, b.monthIndex, b.day);
    return dateA - dateB;
  });

  return soldOutDates;
}

/**
 * Dynamically find sold-out dates between two dates by scanning the calendar
 * This is called at pick-up click time to ensure we catch all sold-out dates
 */
function findSoldOutDatesBetweenDynamic(startDate, endDate, doc) {
  log('findSoldOutDatesBetweenDynamic() called');
  log('  Start (drop-off): ' + formatDateShort(startDate));
  log('  End (attempted pick-up): ' + formatDateShort(endDate));

  var soldOutDates = [];
  var start = new Date(startDate.year, startDate.monthIndex, startDate.day);
  var end = new Date(endDate.year, endDate.monthIndex, endDate.day);

  // If end is before or same as start, no dates in between
  if (end <= start) {
    log('  End date is not after start date - no range to check');
    return soldOutDates;
  }

  // Get all date buttons in the calendar
  var allButtons = doc.querySelectorAll('button.rdp-day');

  for (var i = 0; i < allButtons.length; i++) {
    var btn = allButtons[i];

    // Check if this button is disabled (sold out or past)
    var isDisabled = btn.hasAttribute('disabled') || 
                     btn.classList.contains('rdp-day_disabled') ||
                     btn.classList.contains('disabled');

    if (!isDisabled) continue;

    var dateObj = parseDateFromButton(btn, doc);
    if (!dateObj) continue;

    var btnDate = new Date(dateObj.year, dateObj.monthIndex, dateObj.day);

    // Check if this date is AFTER the drop-off and ON or BEFORE the pick-up
    // (i.e., it falls within the booking range)
    if (btnDate > start && btnDate <= end) {
      // Also verify it's not a past date (must be today or future to be "sold out")
      var today = new Date();
      today.setHours(0, 0, 0, 0);

      if (btnDate >= today) {
        soldOutDates.push({
          day: dateObj.day,
          month: dateObj.month,
          monthIndex: dateObj.monthIndex,
          year: dateObj.year
        });
        log('  Found sold-out date in range: ' + formatDateShort(dateObj));
      }
    }
  }

  // Sort by date
  soldOutDates.sort(function(a, b) {
    var dateA = new Date(a.year, a.monthIndex, a.day);
    var dateB = new Date(b.year, b.monthIndex, b.day);
    return dateA - dateB;
  });

  log('  Total sold-out dates found in range: ' + soldOutDates.length);
  return soldOutDates;
}

/**
 * Cache sold-out dates after drop-off for reference
 * Note: We no longer visually disable dates since we check dynamically at click time
 * This prevents confusing the user by graying out dates that might still be valid drop-off points
 */
function applyCascadeDisable(dropOffDate, doc) {
  log('applyCascadeDisable() called');

  // Find all sold-out dates after drop-off and cache them
  var soldOutDates = findSoldOutDatesAfterDropOff(dropOffDate, doc);

  if (soldOutDates.length === 0) {
    log('  No sold-out dates found after drop-off');
    state.soldOutDatesInRange = [];
    return;
  }

  // Store the sold-out dates for reference
  state.soldOutDatesInRange = soldOutDates;

  log('  Found ' + soldOutDates.length + ' sold-out date(s) after drop-off');
  log('  First sold-out: ' + formatDateShort(soldOutDates[0]));

  // Note: We intentionally do NOT apply visual styling here
  // The modal will handle blocking invalid pick-up selections dynamically
}

/**
 * Clear cached sold-out dates
 */
function removeCascadeDisable(doc) {
  log('removeCascadeDisable() called');
  state.soldOutDatesInRange = [];
  log('  Sold-out date cache cleared');
}

/**
 * Reset the entire booking selection (Start Over functionality)
 */
function resetBookingSelection() {
  log('resetBookingSelection() called - Starting Over');

  // Simply reload the page to clear all selections
  // This is the cleanest way to reset BTA's internal state
  window.location.reload();
}

// ============================================================================
// SOLD-OUT SPANNING MODAL
// ============================================================================

function generateSoldOutSpanningModal(dropOffDate, clickedDate, soldOutDates) {
  var modalId = 'soldout-spanning-modal-' + Date.now();
  var dropOffStr = formatDateShort(dropOffDate);
  var clickedStr = formatDateShort(clickedDate);

  // Format the list of sold-out dates
  var soldOutListItems = '';
  for (var i = 0; i < soldOutDates.length; i++) {
    soldOutListItems += '<li style="margin-bottom: 4px;">• ' + formatDateShort(soldOutDates[i]) + '</li>';
  }
  var dateWord = soldOutDates.length === 1 ? 'date' : 'dates';

  var modalHTML = '\
    <div id="' + modalId + '" class="soldout-spanning-modal" style="\
      display: block;\
      position: fixed;\
      z-index: 999999;\
      left: 0;\
      top: 0;\
      width: 100%;\
      height: 100%;\
      background-color: rgba(0, 0, 0, 0.6);\
      overflow: auto;\
      animation: fadeIn 0.3s ease-out;\
    ">\
      <div class="modal-content" style="\
        background-color: white;\
        margin: 8% auto;\
        padding: 0;\
        border: 3px solid #BD9B60;\
        border-radius: 12px;\
        width: 90%;\
        max-width: 520px;\
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);\
        position: relative;\
        animation: slideDown 0.3s ease-out;\
      ">\
        <div style="\
          background: linear-gradient(135deg, #BD9B60 0%, #a37f4a 100%);\
          padding: 18px 24px;\
          border-radius: 9px 9px 0 0;\
          position: relative;\
        ">\
          <span class="modal-close" style="\
            color: white;\
            position: absolute;\
            right: 20px;\
            top: 14px;\
            font-size: 32px;\
            font-weight: 700;\
            line-height: 24px;\
            cursor: pointer;\
            transition: color 0.2s;\
            opacity: 0.9;\
          ">&times;</span>\
          <div style="font-size: 20px; font-weight: 700; color: white;">\
            Pick-Up Date Unavailable\
          </div>\
          <div style="font-size: 14px; color: rgba(255, 255, 255, 0.9); margin-top: 4px;">\
            Your selection spans sold-out ' + dateWord + '\
          </div>\
        </div>\
        <div style="padding: 24px;">\
          <div style="\
            background: #fef3c7;\
            border: 2px solid #f59e0b;\
            border-radius: 8px;\
            padding: 18px;\
            margin-bottom: 20px;\
          ">\
            <div style="font-size: 15px; color: #78350f; line-height: 1.6;">\
              <strong>We\'re sorry!</strong> Selecting <strong>' + clickedStr + '</strong> as your pick-up date is not possible because your booking would span the following sold-out ' + dateWord + ':\
            </div>\
            <ul style="list-style: none; padding: 0; margin: 16px 0 0 0; color: #78350f; font-weight: 600;">\
              ' + soldOutListItems + '\
            </ul>\
          </div>\
          <div style="\
            background: #f0fdf4;\
            border: 2px solid #86efac;\
            border-radius: 8px;\
            padding: 18px;\
            margin-bottom: 20px;\
          ">\
            <div style="font-size: 16px; font-weight: 600; color: #166534; margin-bottom: 12px;">\
              🐾 Join Our Waitlist\
            </div>\
            <div style="font-size: 15px; color: #166534; line-height: 1.6; margin-bottom: 16px;">\
              Would you like to be notified if a spot opens up? Add yourself to our boarding waitlist and we\'ll contact you if availability changes.\
            </div>\
            <a href="' + CONFIG.waitlistUrl + '" target="_blank" style="\
              display: inline-block;\
              background: #22c55e;\
              color: white;\
              text-decoration: none;\
              padding: 12px 24px;\
              border-radius: 6px;\
              font-weight: 600;\
              font-size: 15px;\
              transition: background 0.2s;\
            " class="waitlist-btn">\
              Join the Waitlist\
            </a>\
            <div style="\
              background: rgba(255, 255, 255, 0.7);\
              border: 1px solid #86efac;\
              border-radius: 6px;\
              padding: 12px;\
              margin-top: 16px;\
            ">\
              <div style="font-size: 13px; color: #166534; line-height: 1.5;">\
                <strong>New customer?</strong> You\'ll need to create a website account first before joining the waitlist.\
                <a href="' + CONFIG.registerUrl + '" target="_blank" style="color: #BD9B60; font-weight: 600; text-decoration: none;"> Create an account →</a>\
              </div>\
            </div>\
          </div>\
          <div style="\
            display: flex;\
            flex-direction: column;\
            gap: 12px;\
            margin-top: 20px;\
          ">\
            <button class="start-over-btn" style="\
              background: #BD9B60;\
              color: white;\
              border: none;\
              padding: 14px 24px;\
              border-radius: 6px;\
              font-weight: 600;\
              font-size: 15px;\
              cursor: pointer;\
              transition: background 0.2s;\
              width: 100%;\
            ">\
              🔄 Start Over - Choose a Different Drop-Off Date\
            </button>\
            <button class="close-btn" style="\
              background: transparent;\
              color: #6b7280;\
              border: 2px solid #d1d5db;\
              padding: 12px 24px;\
              border-radius: 6px;\
              font-weight: 600;\
              font-size: 14px;\
              cursor: pointer;\
              transition: all 0.2s;\
              width: 100%;\
            ">\
              Choose a Different Pick-Up Date\
            </button>\
          </div>\
        </div>\
      </div>\
    </div>\
    <style>\
      @keyframes fadeIn {\
        from { opacity: 0; }\
        to { opacity: 1; }\
      }\
      @keyframes slideDown {\
        from {\
          transform: translateY(-50px);\
          opacity: 0;\
        }\
        to {\
          transform: translateY(0);\
          opacity: 1;\
        }\
      }\
      @keyframes fadeOut {\
        from { opacity: 1; }\
        to { opacity: 0; }\
      }\
      .waitlist-btn:hover {\
        background: #16a34a !important;\
      }\
      .start-over-btn:hover {\
        background: #a37f4a !important;\
      }\
      .close-btn:hover {\
        border-color: #9ca3af !important;\
        color: #374151 !important;\
        background: #f9fafb !important;\
      }\
      .modal-close:hover {\
        opacity: 0.7 !important;\
      }\
      @media (max-width: 480px) {\
        .soldout-spanning-modal .modal-content {\
          margin: 5vh auto !important;\
          width: 95% !important;\
          max-height: 90vh;\
          overflow-y: auto;\
        }\
      }\
    </style>';

  return {
    html: modalHTML,
    modalId: modalId
  };
}

function showSoldOutSpanningModal(dropOffDate, clickedDate, soldOutDates) {
  log('═══════════════════════════════════════════════════════════');
  log('Showing sold-out spanning modal');
  log('  Drop-off: ' + formatDateShort(dropOffDate));
  log('  Attempted pick-up: ' + formatDateShort(clickedDate));

  var modalData = generateSoldOutSpanningModal(dropOffDate, clickedDate, soldOutDates);

  var modalContainer = document.createElement('div');
  modalContainer.innerHTML = modalData.html;
  document.body.appendChild(modalContainer);

  state.modalActive = true;

  var modal = document.getElementById(modalData.modalId);
  var closeBtn = modal.querySelector('.modal-close');
  var closeBtnAlt = modal.querySelector('.close-btn');
  var startOverBtn = modal.querySelector('.start-over-btn');

  var closeModal = function() {
    modal.style.animation = 'fadeOut 0.2s ease-out';
    setTimeout(function() {
      modalContainer.remove();
      state.modalActive = false;
      document.body.style.overflow = 'auto';
      log('Sold-out spanning modal closed');
    }, 200);
  };

  closeBtn.addEventListener('click', closeModal);
  closeBtnAlt.addEventListener('click', closeModal);

  // Start Over button - reset everything
  startOverBtn.addEventListener('click', function() {
    closeModal();
    setTimeout(function() {
      resetBookingSelection();
    }, 250);
  });

  // Close on backdrop click
  modal.addEventListener('click', function(e) {
    if (e.target === modal) {
      closeModal();
    }
  });

  // Close on escape key
  var escapeHandler = function(e) {
    if (e.key === 'Escape' && state.modalActive) {
      closeModal();
      document.removeEventListener('keydown', escapeHandler);
    }
  };
  document.addEventListener('keydown', escapeHandler);

  document.body.style.overflow = 'hidden';

  log('═══════════════════════════════════════════════════════════');
}

function generateSoldOutModal(dateObj) {
  const modalId = 'sold-out-modal-' + Date.now();
  const dateStr = formatDateShort(dateObj);

  // Calculate next day for the message
  const nextDay = new Date(dateObj.year, dateObj.monthIndex, dateObj.day + 1);
  const nextDayStr = formatDateShort({
    day: nextDay.getDate(),
    month: getMonthName(nextDay.getMonth()),
    monthIndex: nextDay.getMonth(),
    year: nextDay.getFullYear()
  });

  const modalHTML = `
    <div id="${modalId}" class="sold-out-modal" style="
      display: block;
      position: fixed;
      z-index: 999999;
      left: 0;
      top: 0;
      width: 100%;
      height: 100%;
      background-color: rgba(0, 0, 0, 0.6);
      overflow: auto;
      animation: fadeIn 0.3s ease-out;
    ">
      <div class="modal-content" style="
        background-color: white;
        margin: 10% auto;
        padding: 0;
        border: 3px solid #BD9B60;
        border-radius: 12px;
        width: 90%;
        max-width: 520px;
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
        position: relative;
        animation: slideDown 0.3s ease-out;
      ">
        <div style="
          background: linear-gradient(135deg, #BD9B60 0%, #a37f4a 100%);
          padding: 18px 24px;
          border-radius: 9px 9px 0 0;
          position: relative;
        ">
          <div style="font-size: 20px; font-weight: 700; color: white;">
            Drop-Off Date Unavailable
          </div>
          <div style="font-size: 14px; color: rgba(255, 255, 255, 0.9); margin-top: 4px;">
            ${dateStr}
          </div>
        </div>

        <div style="padding: 24px;">
          <div style="
            background: #fef3c7;
            border: 2px solid #f59e0b;
            border-radius: 8px;
            padding: 18px;
            margin-bottom: 20px;
          ">
            <div style="font-size: 15px; color: #78350f; line-height: 1.6; margin: 0;">
              <strong>We're sorry!</strong> Selecting ${dateStr} as your drop-off date is not possible because we are currently sold out on ${nextDayStr}. Bookings cannot span a sold-out date.
            </div>
          </div>

          <div style="
            background: #f0fdf4;
            border: 2px solid #86efac;
            border-radius: 8px;
            padding: 18px;
            margin-bottom: 20px;
          ">
            <div style="font-size: 16px; font-weight: 600; color: #166534; margin-bottom: 12px;">
              🐾 Join Our Waitlist
            </div>
            <div style="font-size: 15px; color: #166534; line-height: 1.6; margin-bottom: 16px;">
              Would you like to be notified if a spot opens up? Add yourself to our boarding waitlist and we'll contact you if availability changes.
            </div>
            <a href="${CONFIG.waitlistUrl}" target="_blank" style="
              display: inline-block;
              background: #22c55e;
              color: white;
              text-decoration: none;
              padding: 12px 24px;
              border-radius: 6px;
              font-weight: 600;
              font-size: 15px;
              transition: background 0.2s;
            " class="waitlist-btn">
              Join the Waitlist
            </a>
            <div style="
              background: rgba(255, 255, 255, 0.7);
              border: 1px solid #86efac;
              border-radius: 6px;
              padding: 12px;
              margin-top: 16px;
            ">
              <div style="font-size: 13px; color: #166534; line-height: 1.5;">
                <strong>New customer?</strong> You'll need to create a website account first before joining the waitlist.
                <a href="${CONFIG.registerUrl}" target="_blank" style="color: #BD9B60; font-weight: 600; text-decoration: none;"> Create an account →</a>
              </div>
            </div>
          </div>

          <div style="text-align: center;">
            <button class="close-btn" style="
              background: #6b7280;
              color: white;
              border: none;
              padding: 12px 32px;
              border-radius: 6px;
              font-weight: 600;
              font-size: 15px;
              cursor: pointer;
              transition: background 0.2s;
            ">
              Choose a Different Date
            </button>
          </div>
        </div>
      </div>
    </div>

    <style>
      @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
      }

      @keyframes slideDown {
        from {
          transform: translateY(-50px);
          opacity: 0;
        }
        to {
          transform: translateY(0);
          opacity: 1;
        }
      }

      @keyframes fadeOut {
        from { opacity: 1; }
        to { opacity: 0; }
      }

      .waitlist-btn:hover {
        background: #16a34a !important;
      }

      .close-btn:hover {
        background: #4b5563 !important;
      }

      /* Mobile responsiveness */
      @media (max-width: 480px) {
        .sold-out-modal .modal-content {
          margin: 5vh auto !important;
          width: 95% !important;
          max-height: 90vh;
          overflow-y: auto;
        }
      }
    </style>
  `;

  return {
    html: modalHTML,
    modalId: modalId
  };
}

function showSoldOutModal(dateObj) {
  log('═══════════════════════════════════════════════════════════');
  log('Showing sold-out modal for date:', formatDateShort(dateObj));

  const modalData = generateSoldOutModal(dateObj);

  const modalContainer = document.createElement('div');
  modalContainer.innerHTML = modalData.html;
  document.body.appendChild(modalContainer);

  state.modalActive = true;

  const modal = document.getElementById(modalData.modalId);
  const closeBtn = modal.querySelector('.close-btn');

  const closeModal = () => {
    modal.style.animation = 'fadeOut 0.2s ease-out';
    setTimeout(() => {
      modalContainer.remove();
      state.modalActive = false;
      document.body.style.overflow = 'auto';
      log('Sold-out modal closed');
    }, 200);
  };

  closeBtn.addEventListener('click', closeModal);

  // Close on backdrop click
  modal.addEventListener('click', (e) => {
    if (e.target === modal) {
      closeModal();
    }
  });

  // Close on escape key
  const escapeHandler = (e) => {
    if (e.key === 'Escape' && state.modalActive) {
      closeModal();
      document.removeEventListener('keydown', escapeHandler);
    }
  };
  document.addEventListener('keydown', escapeHandler);

  document.body.style.overflow = 'hidden';

  log('═══════════════════════════════════════════════════════════');
}

// ============================================================================
// MISSING PICK-UP DATE MODAL
// Shows when user clicks Next but has only selected a drop-off date
// ============================================================================

function showMissingPickUpDateModal(dropOffDate) {
  log('═══════════════════════════════════════════════════════════');
  log('showMissingPickUpDateModal() called');
  log(`  Drop-off date: ${dropOffDate.month} ${dropOffDate.day}, ${dropOffDate.year}`);

  const formattedDate = `${dropOffDate.month} ${dropOffDate.day}, ${dropOffDate.year}`;

  const modalHTML = `
    <div id="missing-pickup-modal" style="
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background: rgba(0, 0, 0, 0.7);
      display: flex;
      align-items: center;
      justify-content: center;
      z-index: 999999;
      padding: 20px;
      box-sizing: border-box;
    ">
      <div style="
        background: white;
        border-radius: 12px;
        padding: 24px;
        max-width: 400px;
        width: 100%;
        text-align: center;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
        animation: modalSlideIn 0.3s ease-out;
      ">
        <div style="
          font-size: 48px;
          margin-bottom: 16px;
        ">📅</div>

        <h2 style="
          margin: 0 0 12px 0;
          color: #222;
          font-size: 20px;
          font-weight: 600;
        ">Pick-Up Date Required</h2>

        <p style="
          margin: 0 0 20px 0;
          color: #555;
          font-size: 15px;
          line-height: 1.5;
        ">
          You've selected <strong>${formattedDate}</strong> as your drop-off date, but haven't selected a pick-up date yet.
        </p>

        <p style="
          margin: 0 0 24px 0;
          color: #555;
          font-size: 14px;
          line-height: 1.5;
        ">
          Please select your pick-up date on the calendar. If your pick-up date is in a different month, use the <strong>&gt;</strong> arrow at the top-right of the calendar to navigate to the next month.
        </p>

        <button id="missing-pickup-ok-btn" style="
          background: #BD9B60;
          color: white;
          border: none;
          padding: 14px 32px;
          border-radius: 8px;
          font-size: 16px;
          font-weight: 600;
          cursor: pointer;
          width: 100%;
          transition: background 0.2s;
        ">OK, Go Back to Calendar</button>
      </div>
    </div>

    <style>
      @keyframes modalSlideIn {
        from {
          opacity: 0;
          transform: scale(0.95);
        }
        to {
          opacity: 1;
          transform: scale(1);
        }
      }

      @keyframes arrowPulse {
        0%, 100% {
          transform: scale(1);
          box-shadow: 0 0 0 0 rgba(220, 53, 69, 0.7);
          background-color: rgba(220, 53, 69, 0.2);
        }
        50% {
          transform: scale(1.2);
          box-shadow: 0 0 0 10px rgba(220, 53, 69, 0);
          background-color: rgba(220, 53, 69, 0.5);
        }
      }

      .calendar-arrow-highlight {
        animation: arrowPulse 0.8s ease-in-out 8;
        background-color: rgba(220, 53, 69, 0.3) !important;
        border-radius: 4px;
      }
    </style>
  `;

  // Add modal to page
  const modalContainer = document.createElement('div');
  modalContainer.innerHTML = modalHTML;
  document.body.appendChild(modalContainer);

  // Prevent body scroll
  document.body.style.overflow = 'hidden';

  // Handle OK button click
  const okBtn = document.getElementById('missing-pickup-ok-btn');
  okBtn.addEventListener('click', () => {
    log('  User clicked OK - closing modal and highlighting arrows');

    // Remove modal
    modalContainer.remove();
    document.body.style.overflow = '';

    // Scroll to calendar and highlight arrows
    scrollToCalendarAndHighlightArrows();
  });

  // Handle click outside modal
  const modalOverlay = document.getElementById('missing-pickup-modal');
  modalOverlay.addEventListener('click', (e) => {
    if (e.target === modalOverlay) {
      log('  User clicked outside modal - closing');
      modalContainer.remove();
      document.body.style.overflow = '';
      scrollToCalendarAndHighlightArrows();
    }
  });

  // Handle Escape key
  const escapeHandler = (e) => {
    if (e.key === 'Escape') {
      log('  User pressed Escape - closing modal');
      modalContainer.remove();
      document.body.style.overflow = '';
      document.removeEventListener('keydown', escapeHandler);
      scrollToCalendarAndHighlightArrows();
    }
  };
  document.addEventListener('keydown', escapeHandler);

  log('  Modal displayed');
  log('═══════════════════════════════════════════════════════════');
}

function scrollToCalendarAndHighlightArrows() {
  log('scrollToCalendarAndHighlightArrows() called');

  // Get fresh iframe reference
  const iframe = document.querySelector('#bta-widget-container iframe') || 
                 document.querySelector('iframe[src*="book"]') ||
                 document.querySelector('iframe');

  if (!iframe) {
    log('  ❌ No iframe found');
    return;
  }

  let doc;
  try {
    doc = iframe.contentDocument || iframe.contentWindow?.document;
  } catch (e) {
    log('  ❌ Cannot access iframe document');
    return;
  }

  if (!doc) {
    log('  ❌ No document in iframe');
    return;
  }

  // Find the calendar navigation arrows
  // BTA typically uses buttons with class containing 'nav' or specific arrow characters
  const navButtons = doc.querySelectorAll('button.rdp-nav_button, button[name="previous-month"], button[name="next-month"], .rdp-nav button, button.rdp-button_reset');

  log(`  Found ${navButtons.length} potential nav buttons`);

  // Find the arrow buttons specifically - ONLY the next month (>) button
  // Since user already has a start date, they can only select a later date for pick-up
  let arrowButtons = [];
  navButtons.forEach(btn => {
    const text = btn.textContent.trim();
    const className = btn.className || '';
    const btnName = btn.name || '';
    // Only match the NEXT/forward arrow, not the previous/back arrow
    if (text === '›' || text === '>' || text === '→' || text === '⟩' ||
        btnName.includes('next') || 
        (className.includes('nav') && className.includes('next'))) {
      arrowButtons.push(btn);
    }
  });

  // If we didn't find the next arrow by text/class, look for buttons near the month caption
  // and pick the second one (typically right/next arrow)
  if (arrowButtons.length === 0) {
    const caption = doc.querySelector('.rdp-caption, .rdp-head, [class*="caption"], [class*="header"]');
    if (caption) {
      const nearbyButtons = caption.querySelectorAll('button');
      // Usually the second button is the "next" arrow
      if (nearbyButtons.length >= 2) {
        arrowButtons = [nearbyButtons[nearbyButtons.length - 1]]; // Last button is typically "next"
      }
    }
  }

  log(`  Found ${arrowButtons.length} arrow buttons to highlight`);

  // Scroll the iframe into view first (for mobile)
  if (iframe) {
    // Scroll the main page to bring the iframe into view
    iframe.scrollIntoView({ behavior: 'smooth', block: 'center' });
    log('  Scrolled iframe into view');
  }

  // After a short delay (for scroll to complete), highlight the arrows
  setTimeout(() => {
    arrowButtons.forEach((btn, index) => {
      // Add highlight class
      btn.classList.add('calendar-arrow-highlight');
      log(`  Added highlight to arrow button ${index + 1}`);
     /*
      // Remove highlight after animation completes (8 pulses × 0.8s = 6.4s)
      setTimeout(() => {
        btn.classList.remove('calendar-arrow-highlight');
        log(`  Removed highlight from arrow button ${index + 1}`);
      }, 7000);
      */
    });

    // Also try to inject the animation CSS into the iframe if not already there
    if (doc && !doc.getElementById('arrow-highlight-styles')) {
      const styleEl = doc.createElement('style');
      styleEl.id = 'arrow-highlight-styles';
      styleEl.textContent = `
        @keyframes arrowPulse {
          0%, 100% {
            transform: scale(1);
            box-shadow: 0 0 0 0 rgba(255, 0, 25, 0.7);
            background-color: rgba(220, 53, 69, 0.2);
          }
          50% {
            transform: scale(1.2);
            box-shadow: 0 0 0 10px rgba(220, 53, 69, 0);
            background-color: rgba(255, 0, 25, 0.5);
          }
        }

        .calendar-arrow-highlight {
          animation: arrowPulse 1.8s ease-in-out infinite !important;
          background-color: rgba(220, 53, 69, 0.3) !important;
          border-radius: 4px !important;
          position: relative;
          z-index: 100;
        }
      `;
      doc.head.appendChild(styleEl);
      log('  Injected animation CSS into iframe');
    }
  }, 400); // Wait for scroll to mostly complete

  log('═══════════════════════════════════════════════════════════');
}

// ============================================================================
// FORM INTERCEPTION & PROPERTY INJECTION
// ============================================================================

function initializeFormInterception() {
  log('═══════════════════════════════════════════════════════════');
  log('Initializing form interception for property injection');

  // Clear any existing interval before creating a new one.
  // Without this, every call (including from the watcher) stacks up a new
  // 500ms polling loop that runs forever, causing severe performance degradation.
  if (state.nextButtonInterval) {
    clearInterval(state.nextButtonInterval);
    state.nextButtonInterval = null;
    log('Cleared previous checkForNextButton interval');
  }

  let lastButtonText = null;
  let lastButtonElement = null;  // Track the actual button element, not just text

  state.nextButtonInterval = setInterval(() => {
    // Get fresh iframe doc each time - don't use cached version
    // This is important because BTA may rebuild the iframe when navigating months
    const iframe = document.querySelector('#bta-widget-container iframe') || 
                   document.querySelector('iframe[src*="book"]') ||
                   document.querySelector('iframe');
    if (!iframe) return;

    let doc;
    try {
      doc = iframe.contentDocument || iframe.contentWindow?.document;
    } catch (e) {
      return; // Cross-origin or not accessible
    }
    if (!doc) return;

    // Find submit button or any button with "Next" or "Book" text
    let nextButton = doc.querySelector('button[type="submit"]');

    if (!nextButton) {
      const buttons = doc.querySelectorAll('button');
      for (const btn of buttons) {
        const text = btn.textContent.trim().toLowerCase();
        if (text.includes('next') || text.includes('book')) {
          nextButton = btn;
          break;
        }
      }
    }

    if (!nextButton) return;

    const currentButtonText = nextButton.textContent.trim();

    // Re-attach listener if button changed OR if it's a different element (DOM was rebuilt)
    if (currentButtonText !== lastButtonText || nextButton !== lastButtonElement) {
      log(`✓ Found button: "${currentButtonText}"${nextButton !== lastButtonElement ? ' (new element)' : ''}`);

      // Remove old listener if exists
      if (timeState.nextButtonListener && timeState.lastButton) {
        timeState.lastButton.removeEventListener('click', timeState.nextButtonListener, true);
        log('  Removed old listener from previous button');
      }

      lastButtonText = currentButtonText;
      lastButtonElement = nextButton;
      timeState.lastButton = nextButton;

      timeState.nextButtonListener = async (e) => {
        log('═══════════════════════════════════════════════════════════');
        log('NEXT BUTTON CLICKED');
        log(`  Button text: "${e.target.textContent.trim()}"`);

        // Check if user has selected drop-off but not pick-up date
        // This happens when they click Next thinking it navigates the calendar
        if (timeState.dropOffDate && timeState.dropOffTime && (!timeState.pickUpDate || !timeState.pickUpTime)) {
          log('⚠️ User has drop-off date but no pick-up date - showing helper modal');
          telNotable('missing_pickup_modal', {
            dropOff: `${timeState.dropOffDate.year}-${timeState.dropOffDate.monthIndex + 1}-${timeState.dropOffDate.day}`
          });
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();

          showMissingPickUpDateModal(timeState.dropOffDate);
          return;
        }

        if (!timeState.dropOffTime || !timeState.pickUpTime) {
          log('❌ Missing time selections, allowing normal flow');
          log(`  Drop-off time: ${timeState.dropOffTime || 'NOT SET'}`);
          log(`  Pick-up time: ${timeState.pickUpTime || 'NOT SET'}`);
          return;
        }

        // FIX: Ensure dates are in correct chronological order
        // Drop-off (earlier date) should be BEFORE pick-up (later date)
        // This handles cases where click count got mixed up due to user clicking other elements
        if (timeState.dropOffDate && timeState.pickUpDate) {
          const dropOffDateObj = new Date(timeState.dropOffDate.year, timeState.dropOffDate.monthIndex, timeState.dropOffDate.day);
          const pickUpDateObj = new Date(timeState.pickUpDate.year, timeState.pickUpDate.monthIndex, timeState.pickUpDate.day);

          if (dropOffDateObj > pickUpDateObj) {
            log('⚠️ Dates are in wrong order - SWAPPING to correct chronological order');
            log(`  Before swap: dropOff=${formatDateShort(timeState.dropOffDate)}, pickUp=${formatDateShort(timeState.pickUpDate)}`);

            // Swap the dates and times
            const tempDate = timeState.dropOffDate;
            const tempTime = timeState.dropOffTime;
            timeState.dropOffDate = timeState.pickUpDate;
            timeState.dropOffTime = timeState.pickUpTime;
            timeState.pickUpDate = tempDate;
            timeState.pickUpTime = tempTime;

            log(`  After swap: dropOff=${formatDateShort(timeState.dropOffDate)}, pickUp=${formatDateShort(timeState.pickUpDate)}`);
          }
        }

        log('✓ Time selections complete:');
        log(`  Drop-off: ${getDayName(new Date(timeState.dropOffDate.year, timeState.dropOffDate.monthIndex, timeState.dropOffDate.day))}, ${formatDateShort(timeState.dropOffDate)} at ${timeState.dropOffTime}`);
        log(`  Pick-up: ${getDayName(new Date(timeState.pickUpDate.year, timeState.pickUpDate.monthIndex, timeState.pickUpDate.day))}, ${formatDateShort(timeState.pickUpDate)} at ${timeState.pickUpTime}`);

        // v4.7: final check that BTA's calendar matches these dates
        if (!verifyBtaBeforeNext()) {
          log('🛑 BTA selection does not match - blocking Next');
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();
          showCalendarResyncModal('next', null);
          return;
        }

        const radioSelection = `${timeState.dropOffTime}-${timeState.pickUpTime}`;
        log(`  Radio selection needed: ${radioSelection}`);

        // ── FLEX BOOKING MODAL ───────────────────────────────────────────
        // Show before allowing BTA to proceed to Step 2.
        // Only shown once per booking session; resets if widget is rebuilt.
        if (!flexState.shown) {
          e.preventDefault();
          e.stopPropagation();
          e.stopImmediatePropagation();

          log('Flex: modal not yet shown — intercepting Next click');

          // Read booking amount from iframe price element
          const freshDoc = getIframeDoc();
          const bookingAmount = getBookingAmountFromIframe(freshDoc);

          // Show modal and await customer decision
          const decision = await showFlexModal(bookingAmount);
          flexState.decision = decision;
          flexState.shown = true;

          log(`Flex: decision="${decision}", triggering Next button to proceed`);

          // Start polling for Step 2 radios BEFORE clicking Next,
          // so the poller is ready when BTA navigates
          waitForRadiosAndSelect(radioSelection);

          // Now programmatically click the Next button to trigger BTA navigation
          // Use a fresh reference since the modal removal may have shifted focus
          const currentDoc = getIframeDoc();
          if (currentDoc) {
            let btn = currentDoc.querySelector('button[type="submit"]');
            if (!btn) {
              const buttons = currentDoc.querySelectorAll('button');
              for (const b of buttons) {
                const t = b.textContent.trim().toLowerCase();
                if (t.includes('next') || t.includes('book')) { btn = b; break; }
              }
            }
            if (btn) {
              log('Flex: clicking Next button programmatically');
              btn.click();
            } else {
              log('⚠ Flex: could not find Next button to click programmatically');
            }
          }
          return;
        }
        // ── END FLEX BOOKING MODAL ───────────────────────────────────────

        // DON'T block the click - let BTA navigate to Step 2
        // Then poll for radios to appear and select the correct one
        log('✓ Allowing Next click to proceed, will select radio after Step 2 loads...');

        waitForRadiosAndSelect(radioSelection);
      };

      nextButton.addEventListener('click', timeState.nextButtonListener, true);
      log('✓ Interception listener installed (capture phase)');
      log('═══════════════════════════════════════════════════════════');
    }
  }, 500);
}

function waitForRadiosAndSelect(radioSelection) {
  log('═══════════════════════════════════════════════════════════');
  log('waitForRadiosAndSelect() - Polling for Step 2 radios...');
  log(`  Target selection: ${radioSelection}`);

  let attempts = 0;
  const maxAttempts = 60; // 3 seconds max at 50ms intervals

  const pollInterval = setInterval(() => {
    attempts++;

    // Get fresh iframe doc
    const iframe = document.querySelector('iframe[title="widget_xcomponent"]');
    if (!iframe) {
      log(`  Attempt ${attempts}: No iframe found`);
      if (attempts >= maxAttempts) {
        clearInterval(pollInterval);
        log('❌ Gave up waiting for iframe');
      }
      return;
    }

    let doc;
    try {
      doc = iframe.contentDocument || iframe.contentWindow?.document;
    } catch (e) {
      log(`  Attempt ${attempts}: Cannot access iframe - ${e.message}`);
      if (attempts >= maxAttempts) {
        clearInterval(pollInterval);
        log('❌ Gave up waiting for iframe access');
      }
      return;
    }

    if (!doc) {
      log(`  Attempt ${attempts}: No document in iframe`);
      if (attempts >= maxAttempts) {
        clearInterval(pollInterval);
        log('❌ Gave up waiting for document');
      }
      return;
    }

    // Check for radios
    const radios = doc.querySelectorAll('input[type="radio"][name="drop_off_and_pick_up_time"]');
    log(`  Attempt ${attempts}: Found ${radios.length} radio inputs`);

    if (radios.length > 0) {
      clearInterval(pollInterval);
      log(`✓ Radios found after ${attempts} attempts!`);

      // Minimal delay to ensure DOM is ready
      setTimeout(() => {
        selectRadioAndSubmit(doc, radioSelection);
      }, 10);
      return;
    }

    if (attempts >= maxAttempts) {
      clearInterval(pollInterval);
      log('❌ Gave up waiting for radios after 3 seconds');
    }
  }, 50);  // Poll every 50ms for faster response
}

function selectRadioAndSubmit(doc, radioSelection) {
  log('═══════════════════════════════════════════════════════════');
  log('selectRadioAndSubmit() called');
  log(`  Target selection: ${radioSelection}`);

  // BTA uses real input[type="radio"] elements with name="drop_off_and_pick_up_time"
  // Values are: AM-AM, AM-PM, PM-AM, PM-PM
  const radios = doc.querySelectorAll('input[type="radio"][name="drop_off_and_pick_up_time"]');
  log(`  Found ${radios.length} radio inputs`);

  let targetRadio = null;

  radios.forEach((radio, index) => {
    const value = radio.value || '';
    log(`  Radio ${index + 1}: value="${value}"`);

    if (value === radioSelection) {
      targetRadio = radio;
      log(`  ✓ MATCH FOUND: Radio ${index + 1} with value="${value}"`);
    }
  });

  if (targetRadio) {
    log('✓ Target radio input found, selecting via label click...');

    // Find the parent label that wraps this radio
    // The label might be a sibling or parent - check both
    let label = targetRadio.closest('label');
    if (!label) {
      // Label might be a sibling with for attribute, or the radio might be inside a label
      const parent = targetRadio.parentElement;
      if (parent && parent.tagName === 'LABEL') {
        label = parent;
      } else {
        // Look for label with matching for attribute
        const radioId = targetRadio.id;
        if (radioId) {
          label = doc.querySelector(`label[for="${radioId}"]`);
        }
        // Or look for sibling label
        if (!label) {
          label = parent?.querySelector('label');
        }
      }
    }

    if (label) {
      log('  ✓ Found label element, clicking label first...');
      label.click();
      log('  ✓ Label clicked');
    } else {
      log('  ⚠ No label found, clicking radio directly...');
      targetRadio.click();
    }

    // Wait for the selection to register, then click Book Now
    setTimeout(() => {
      // Verify the radio is actually selected
      log(`  Radio checked state: ${targetRadio.checked}`);

      // Find the Book Now button
      let bookButton = null;
      const buttons = doc.querySelectorAll('button');
      for (const btn of buttons) {
        const text = btn.textContent.trim().toLowerCase();
        if (text.includes('book')) {
          bookButton = btn;
          break;
        }
      }

      if (!bookButton) {
        bookButton = doc.querySelector('button[data-type="submit"]');
      }

      if (bookButton) {
        log('✓ Found Book Now button, clicking...');
        bookButton.click();
        log('✓ Book Now clicked');
      } else {
        log('❌ Could not find Book Now button');
      }
    }, 30);
  } else {
    log('❌ Could not find matching radio element');
    log('  Available options:');
    radios.forEach((radio, index) => {
      log(`    ${index + 1}: value="${radio.value}"`);
    });
  }

  log('═══════════════════════════════════════════════════════════');
}

function injectPropertiesIntoBooking() {
  log('═══════════════════════════════════════════════════════════');
  log('injectPropertiesIntoBooking() called');

  if (!timeState.dropOffTime || !timeState.pickUpTime) {
    log('❌ Missing time selections, cannot inject properties');
    return;
  }

  // Convert custom date objects to JavaScript Date objects
  const dropOffDate = new Date(timeState.dropOffDate.year, timeState.dropOffDate.monthIndex, timeState.dropOffDate.day);
  const pickUpDate = new Date(timeState.pickUpDate.year, timeState.pickUpDate.monthIndex, timeState.pickUpDate.day);

  const dropOffWindows = getTimeWindows(dropOffDate, false);
  const pickUpWindows = getTimeWindows(pickUpDate, true);

  const dropOffTimeWindow = dropOffWindows[timeState.dropOffTime];
  const pickUpTimeWindow = pickUpWindows[timeState.pickUpTime];

  const properties = {
    'dropOffTime': `${dropOffTimeWindow.start} - ${dropOffTimeWindow.end}`,
    'dropOffDay': getDayName(dropOffDate),
    'dropOffDate': formatDateShort(timeState.dropOffDate),
    'pickUpTime': `${pickUpTimeWindow.start} - ${pickUpTimeWindow.end}`,
    'pickUpDay': getDayName(pickUpDate),
    'pickUpDate': formatDateShort(timeState.pickUpDate)
  };

  log('✓ Properties created:');
  Object.entries(properties).forEach(([key, value]) => {
    log(`  ${key}: ${value}`);
  });

  const doc = getIframeDoc();
  if (!doc) {
    log('❌ No iframe document available - BTA widget may not be ready');
    return;
  }

  const form = doc.querySelector('form');
  if (!form) {
    log('❌ No form found in iframe');
    return;
  }

  Object.entries(properties).forEach(([key, value]) => {
    const input = doc.createElement('input');
    input.type = 'hidden';
    input.name = `properties[${key}]`;
    input.value = value;
    form.appendChild(input);
    log(`  ✓ Added hidden input: properties[${key}] = ${value}`);
  });

  log('✓ All properties injected into form');
  log('═══════════════════════════════════════════════════════════');
}

// =============================================================================
// [MODALS MODULE - Keeping existing modal code from original bundle]
// This section contains the Dec 24 and restriction modals
// =============================================================================

// NOTE: I'm keeping this section minimal to fit within file size limits
// The modals module code from your original bundle goes here
// For now, I'll include placeholder functions that match the interface

// ============================================================================
// DEC 24TH INFO MODAL GENERATION
// ============================================================================

function generateDec24InfoModal(dateObj, isDropOff) {
  const dateType = isDropOff ? 'drop-off' : 'pick-up';
  const modalId = 'holiday-modal-dec24-' + Date.now();

  const modalHTML = `
    <div id="${modalId}" class="holiday-modal" style="
      display: block;
      position: fixed;
      z-index: 999999;
      left: 0;
      top: 0;
      width: 100%;
      height: 100%;
      background-color: rgba(0, 0, 0, 0.6);
      overflow: auto;
      animation: fadeIn 0.3s ease-out;
    ">
      <div class="modal-content" style="
        background-color: white;
        margin: 10% auto;
        padding: 0;
        border: 3px solid #f59e0b;
        border-radius: 12px;
        width: 90%;
        max-width: 480px;
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
        position: relative;
        animation: slideDown 0.3s ease-out;
      ">
        <!-- Header -->
        <div style="
          background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
          padding: 18px 24px;
          border-radius: 9px 9px 0 0;
          position: relative;
        ">
          <div style="font-size: 20px; font-weight: 700; color: white;">
            December 24th - AM Service Only
          </div>
        </div>

        <!-- Body -->
        <div style="padding: 24px;">
          <div style="
            background: #fffbeb;
            border: 2px solid #fbbf24;
            border-radius: 8px;
            padding: 18px;
            margin-bottom: 20px;
          ">
            <div style="font-size: 15px; color: #78350f; line-height: 1.6; margin: 0;">
              ⚠️ Please note: We provide <strong>AM ${dateType} service only</strong> on December 24th.
            </div>
          </div>

          <div style="text-align: center;">
            <button class="ok-btn" style="
              background: #f59e0b;
              color: white;
              border: none;
              padding: 12px 32px;
              border-radius: 6px;
              font-weight: 600;
              font-size: 15px;
              cursor: pointer;
              transition: background 0.2s;
              min-width: 120px;
            ">
              OK, Continue
            </button>
          </div>
        </div>
      </div>
    </div>

    <style>
      @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
      }

      @keyframes slideDown {
        from {
          transform: translateY(-50px);
          opacity: 0;
        }
        to {
          transform: translateY(0);
          opacity: 1;
        }
      }

      .ok-btn:hover {
        background: #d97706 !important;
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);
      }

      /* Mobile responsiveness */
      @media (max-width: 480px) {
        .holiday-modal .modal-content {
          margin: 5vh auto !important;
          width: 95% !important;
          max-height: 90vh;
          overflow-y: auto;
        }
      }
    </style>
  `;

  return {
    html: modalHTML,
    modalId: modalId
  };
}

function generateModalContent(restriction, dateObj, isDropOff, alternatives, firstSelectedDate) {
  const dateType = isDropOff ? 'drop-off' : 'pick-up';
  const modalId = 'holiday-modal-' + Date.now();

  // Filter alternatives based on selected dates and minimum 1-night stay
  let validBefore = null;
  let validAfter = null;

  if (isDropOff) {
    // For drop-off: exclude alternatives that are same as or after pick-up (rare case)
    // In practice, pick-up isn't selected yet, so both alternatives should be valid
    validBefore = alternatives.before;
    validAfter = alternatives.after;
  } else {
    // For pick-up: exclude alternatives that are same as or before drop-off (minimum 1 night)
    if (firstSelectedDate) {
      const dropOffDate = new Date(firstSelectedDate.year, firstSelectedDate.monthIndex, firstSelectedDate.day);

      // Check "before" alternative
      if (alternatives.before) {
        const beforeDate = new Date(alternatives.before.year, alternatives.before.monthIndex, alternatives.before.day);
        if (beforeDate > dropOffDate) {
          validBefore = alternatives.before;
        }
      }

      // Check "after" alternative
      if (alternatives.after) {
        const afterDate = new Date(alternatives.after.year, alternatives.after.monthIndex, alternatives.after.day);
        if (afterDate > dropOffDate) {
          validAfter = alternatives.after;
        }
      }
    } else {
      // If no drop-off date stored (shouldn't happen), show both
      validBefore = alternatives.before;
      validAfter = alternatives.after;
    }
  }

  // Count valid alternatives
  const validCount = (validBefore ? 1 : 0) + (validAfter ? 1 : 0);
  const hasOnlyOne = validCount === 1;

  // Determine which holiday content to show
  const isChristmas = dateObj.monthIndex === 11 && dateObj.day >= 24 && dateObj.day <= 26;
  const isNewYears = dateObj.monthIndex === 0 && dateObj.day === 1;

  let holidayContent = '';

  if (isChristmas) {
    holidayContent = `
      <div style="font-size: 18px; font-weight: 600; color: #92400e; margin-bottom: 12px;">🎄 Holiday Hours</div>
      <p style="color: #374151; margin: 0 0 16px 0; font-size: 15px; line-height: 1.6;">We are caring for dogs throughout the holiday! However, our pick-up and drop-off windows are adjusted to allow our staff time with their families. This means to book during this time, you will need to drop your dog off with us on or before December 24th in the morning and pick your dog up on or after December 27th.</p>
      <div style="color: #374151; margin-bottom: 12px; font-size: 15px; line-height: 1.8;">📅 <strong>Last available Pick up / Drop off before the Holidays:</strong><br>&nbsp;&nbsp;&nbsp;&nbsp;December 24th, 7:00 AM - 8:45 AM</div>
      <div style="color: #374151; margin-bottom: 16px; font-size: 15px; line-height: 1.8;">📅 <strong>First available Pick up / Drop off after the Holidays:</strong><br>&nbsp;&nbsp;&nbsp;&nbsp;December 27th, 7:00 AM - 8:45 AM</div>
      <p style="color: #374151; margin: 0 0 16px 0; font-size: 14px; line-height: 1.6;">Your dog is welcome to stay with us over these dates. <strong>Note:</strong> December 24th PM window and all of December 25th-26th are not available for pick-ups or drop-offs.</p>
    `;
  } else if (isNewYears) {
    holidayContent = `
      <div style="font-size: 18px; font-weight: 600; color: #92400e; margin-bottom: 12px;">🎊 Holiday Hours</div>
      <p style="color: #374151; margin: 0 0 16px 0; font-size: 15px; line-height: 1.6;">Ring in the new year worry-free! We'll be caring for dogs throughout the holiday. However, our pick-up and drop-off windows are adjusted so our staff can celebrate:</p>
      <div style="color: #374151; margin-bottom: 12px; font-size: 15px; line-height: 1.8;">📅 <strong>Last available Pick up / Drop off before the Holidays:</strong><br>&nbsp;&nbsp;&nbsp;&nbsp;December 31st, 5:00 PM - 5:45 PM</div>
      <div style="color: #374151; margin-bottom: 16px; font-size: 15px; line-height: 1.8;">📅 <strong>First available Pick up / Drop off after the Holidays:</strong><br>&nbsp;&nbsp;&nbsp;&nbsp;January 2nd, 7:00 AM - 8:45 AM</div>
      <p style="color: #374151; margin: 0 0 16px 0; font-size: 14px; line-height: 1.6;">Your dog is welcome to stay with us over New Year's Day. <strong>Note:</strong> January 1st is not available for pick-ups or drop-offs.</p>
    `;
  }

  let restrictionMessage = '';

  if (restriction.type === 'AM_ONLY') {
    restrictionMessage = `
      <div style="background: #fef3c7; border: 2px solid #f59e0b; border-radius: 8px; padding: 16px; margin-bottom: 16px;">
        <div style="font-size: 16px; font-weight: 600; color: #78350f; margin-bottom: 8px;">⚠️ Holiday Restrictions</div>
        <p style="color: #78350f; font-size: 14px; line-height: 1.6; margin: 0;">
          You've selected <strong>${formatDateShort(dateObj)}</strong> for your <strong>${dateType}</strong>. 
          Only <strong>AM time windows (7:00 AM - 8:45 AM)</strong> are available on December 24th.
        </p>
      </div>
    `;
  } else {
    restrictionMessage = `
      <div style="background: #fef3c7; border: 2px solid #f59e0b; border-radius: 8px; padding: 16px; margin-bottom: 16px;">
        <div style="font-size: 16px; font-weight: 600; color: #78350f; margin-bottom: 8px;">⚠️ Holiday Restrictions</div>
        <p style="color: #78350f; font-size: 14px; line-height: 1.6; margin: 0;">
          You've selected <strong>${formatDateShort(dateObj)}</strong> for your <strong>${dateType}</strong>. 
          Restrictions apply for pick-ups and drop-offs during the holiday timeframe.
        </p>
      </div>
    `;
  }

  const modalHTML = `
    <div id="${modalId}" class="holiday-modal" style="
      display: block;
      position: fixed;
      z-index: 999999;
      left: 0;
      top: 0;
      width: 100%;
      height: 100%;
      background-color: rgba(0, 0, 0, 0.6);
      overflow: auto;
      animation: fadeIn 0.3s ease-out;
    ">
      <div class="modal-content" style="
        background-color: white;
        margin: 5% auto;
        padding: 0;
        border: 3px solid #f59e0b;
        border-radius: 12px;
        width: 90%;
        max-width: 600px;
        box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
        position: relative;
        animation: slideDown 0.3s ease-out;
      ">
        <!-- Header -->
        <div style="
          background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
          padding: 20px 24px;
          border-radius: 9px 9px 0 0;
          position: relative;
        ">
          <span class="modal-close" style="
            color: white;
            position: absolute;
            right: 20px;
            top: 16px;
            font-size: 32px;
            font-weight: 700;
            line-height: 24px;
            cursor: pointer;
            transition: color 0.2s;
            opacity: 0.9;
          ">&times;</span>
          <div style="font-size: 24px; font-weight: 700; color: white; margin-bottom: 4px;">
            Holiday Schedule Notice
          </div>
          <div style="font-size: 14px; color: rgba(255, 255, 255, 0.9);">
            Important information about your selected ${dateType} date
          </div>
        </div>

        <!-- Body -->
        <div style="padding: 24px;">
          ${restrictionMessage}

          ${holidayContent}

          <!-- Alternative Dates -->
          <div style="
            background: #fffbeb;
            border: 2px solid #fbbf24;
            border-radius: 8px;
            padding: 20px;
            margin-top: 20px;
          ">
            <div style="font-size: 16px; font-weight: 600; color: #92400e; margin-bottom: 16px;">
              ✅ Available Alternative ${hasOnlyOne ? 'Date' : 'Dates'}
            </div>

            <div style="display: flex; flex-direction: column; gap: 12px;">
              ${validBefore ? `
              <!-- Before Option -->
              <div style="
                background: white;
                border: 2px solid #fde68a;
                border-radius: 8px;
                padding: 16px;
                transition: all 0.2s;
                cursor: pointer;
              " class="date-option" data-direction="before">
                <div style="display: flex; justify-content: space-between; align-items: center;">
                  <div>
                    <div style="font-size: 16px; font-weight: 600; color: #92400e; margin-bottom: 4px;">
                      📅 ${formatAlternativeDate(validBefore)}
                    </div>
                    <div style="font-size: 13px; color: #6b7280;">
                      or any ${dateType} date before this.
                    </div>
                  </div>
                  <button class="select-date-btn" data-direction="before" style="
                    background: #f59e0b;
                    color: white;
                    border: none;
                    padding: 10px 20px;
                    border-radius: 6px;
                    font-weight: 600;
                    font-size: 14px;
                    cursor: pointer;
                    transition: background 0.2s;
                    white-space: nowrap;
                  ">
                    Select This ${dateType} Date
                  </button>
                </div>
              </div>
              ` : ''}

              ${validBefore && validAfter ? '<div style="text-align: center; color: #9ca3af; font-weight: 600;">— OR —</div>' : ''}

              ${validAfter ? `
              <!-- After Option -->
              <div style="
                background: white;
                border: 2px solid #fde68a;
                border-radius: 8px;
                padding: 16px;
                transition: all 0.2s;
                cursor: pointer;
              " class="date-option" data-direction="after">
                <div style="display: flex; justify-content: space-between; align-items: center;">
                  <div>
                    <div style="font-size: 16px; font-weight: 600; color: #92400e; margin-bottom: 4px;">
                      📅 ${formatAlternativeDate(validAfter)}
                    </div>
                    <div style="font-size: 13px; color: #6b7280;">
                      or any ${dateType} date after this.
                    </div>
                  </div>
                  <button class="select-date-btn" data-direction="after" style="
                    background: #f59e0b;
                    color: white;
                    border: none;
                    padding: 10px 20px;
                    border-radius: 6px;
                    font-weight: 600;
                    font-size: 14px;
                    cursor: pointer;
                    transition: background 0.2s;
                    white-space: nowrap;
                  ">
                    Select This ${dateType} Date
                  </button>
                </div>
              </div>
              ` : ''}
            </div>

            <div style="
              margin-top: 16px;
              padding-top: 16px;
              border-top: 1px solid #fde68a;
              text-align: center;
            ">
              <button class="cancel-btn" style="
                background: transparent;
                color: #6b7280;
                border: 2px solid #d1d5db;
                padding: 10px 24px;
                border-radius: 6px;
                font-weight: 600;
                font-size: 14px;
                cursor: pointer;
                transition: all 0.2s;
              ">
                I'll Choose a Different ${dateType} Date
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>

    <style>
      @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
      }

      @keyframes slideDown {
        from {
          transform: translateY(-50px);
          opacity: 0;
        }
        to {
          transform: translateY(0);
          opacity: 1;
        }
      }

      .date-option:hover {
        border-color: #f59e0b !important;
        box-shadow: 0 4px 12px rgba(245, 158, 11, 0.2);
      }

      .select-date-btn:hover {
        background: #d97706 !important;
        transform: translateY(-1px);
        box-shadow: 0 4px 12px rgba(245, 158, 11, 0.3);
      }

      .cancel-btn:hover {
        border-color: #9ca3af !important;
        color: #374151 !important;
        background: #f9fafb !important;
      }

      .modal-close:hover {
        color: #fecaca !important;
        opacity: 1 !important;
      }

      /* Mobile responsiveness */
      @media (max-width: 480px) {
        .holiday-modal .modal-content {
          margin: 3vh auto !important;
          width: 95% !important;
          max-height: 94vh;
          overflow-y: auto;
        }

        .date-option {
          flex-direction: column !important;
        }

        .date-option > div {
          flex-direction: column !important;
          align-items: flex-start !important;
          gap: 12px;
        }

        .select-date-btn {
          width: 100% !important;
          text-align: center !important;
        }
      }
    </style>
  `;

  return {
    html: modalHTML,
    modalId: modalId,
    alternatives: {
      before: validBefore,
      after: validAfter
    }
  };
}

// ============================================================================
// MODAL DISPLAY FUNCTIONS
// ============================================================================

function showDec24InfoModalNonBlocking(modalData, dateObj, isDropOff) {
  // Check if a Dec 24 info modal is already open
  const existingModal = document.querySelector('.holiday-modal');
  if (existingModal) {
    log('Dec 24th info modal already open - skipping duplicate');
    return;
  }

  // Insert modal into main document (not iframe)
  const modalContainer = document.createElement('div');
  modalContainer.innerHTML = modalData.html;
  document.body.appendChild(modalContainer);

  state.modalActive = true;

  const modal = document.getElementById(modalData.modalId);
  const okBtn = modal.querySelector('.ok-btn');

  // OK button handler - just close the modal, date is already selected
  const handleOk = (e) => {
    if (e) {
      e.stopPropagation();
    }

    modal.style.animation = 'fadeOut 0.2s ease-out';
    setTimeout(() => {
      modalContainer.remove();
      state.modalActive = false;
      document.body.style.overflow = 'auto';
      log('Dec 24th info modal closed');
    }, 200);
  };

  okBtn.addEventListener('click', handleOk, { once: true });

  // Close on escape key
  const escapeHandler = (e) => {
    if (e.key === 'Escape' && state.modalActive) {
      handleOk();
      document.removeEventListener('keydown', escapeHandler);
    }
  };
  document.addEventListener('keydown', escapeHandler);

  // Prevent body scroll while modal is open
  document.body.style.overflow = 'hidden';

  // Add fadeOut animation for smooth closing
  const style = document.createElement('style');
  style.textContent = `
    @keyframes fadeOut {
      from { opacity: 1; }
      to { opacity: 0; }
    }
  `;
  if (!document.getElementById('modal-fade-out-style')) {
    style.id = 'modal-fade-out-style';
    document.head.appendChild(style);
  }
}


function showModal(modalData, restrictedDate, isDropOff) {
  // Insert modal into main document (not iframe)
  const modalContainer = document.createElement('div');
  modalContainer.innerHTML = modalData.html;
  document.body.appendChild(modalContainer);

  state.modalActive = true;

  const modal = document.getElementById(modalData.modalId);
  const closeBtn = modal.querySelector('.modal-close');
  const cancelBtn = modal.querySelector('.cancel-btn');
  const selectBtns = modal.querySelectorAll('.select-date-btn');

  // Close handlers
  const closeModal = () => {
    modal.style.animation = 'fadeOut 0.2s ease-out';
    setTimeout(() => {
      modalContainer.remove();
      state.modalActive = false;
      document.body.style.overflow = 'auto';
    }, 200);
  };

  closeBtn.addEventListener('click', closeModal);
  cancelBtn.addEventListener('click', closeModal);

  // Close on backdrop click
  modal.addEventListener('click', (e) => {
    if (e.target === modal) {
      closeModal();
    }
  });

  // Close on escape key
  const escapeHandler = (e) => {
    if (e.key === 'Escape' && state.modalActive) {
      closeModal();
      document.removeEventListener('keydown', escapeHandler);
    }
  };
  document.addEventListener('keydown', escapeHandler);

  // Select date buttons
  selectBtns.forEach(btn => {
    btn.addEventListener('click', (e) => {
      e.stopPropagation();
      const direction = btn.getAttribute('data-direction');
      const suggestedDate = direction === 'before' 
        ? modalData.alternatives.before 
        : modalData.alternatives.after;

      log(`User selected suggested date: ${formatDateShort(suggestedDate)} (${direction})`);

      // Store the suggested date and close modal
      state.pendingSuggestedDate = suggestedDate;
      closeModal();

      // Navigate to the correct month if needed, then click the date
      setTimeout(() => {
        selectDateInCalendar(suggestedDate);
      }, 300);
    });
  });

  // Prevent body scroll while modal is open
  document.body.style.overflow = 'hidden';

  // Add fadeOut animation for smooth closing
  const style = document.createElement('style');
  style.textContent = `
    @keyframes fadeOut {
      from { opacity: 1; }
      to { opacity: 0; }
    }
  `;
  document.head.appendChild(style);
}

// ============================================================================
// CALENDAR UTILS (for alternative date selection)
// ============================================================================

function selectDateInCalendar(dateObj) {
  log('selectDateInCalendar() called for:', formatDateShort(dateObj));
  // Implementation for selecting alternative dates
}

// ============================================================================
// IFRAME ACCESS
// ============================================================================

function getIframeDoc() {
  // Never cache - always get a fresh reference so we don't operate on a
  // stale document if BTA rebuilds its widget after the page first loads.
  log('getIframeDoc() called - fetching fresh reference');

  const selectors = [
    'iframe[title="widget_xcomponent"]',
    'iframe[id*="bta-product-widget"]',
    'iframe[src*="book.app"]',
    'iframe[src*="bookthatapp"]',
    '#bta-widget-container iframe',
    'iframe'
  ];

  let iframe = null;
  let usedSelector = null;

  for (const selector of selectors) {
    const candidates = document.querySelectorAll(selector);
    for (const candidate of candidates) {
      if (candidate.offsetWidth > 0 && candidate.offsetHeight > 0) {
        iframe = candidate;
        usedSelector = selector;
        break;
      }
    }
    if (iframe) break;
  }

  if (!iframe) {
    log('❌ No visible iframe found');
    return null;
  }

  log(`✓ Found iframe with selector: ${usedSelector}`);

  try {
    const doc = iframe.contentDocument || iframe.contentWindow?.document;
    if (doc) {
      log('✓ Successfully accessed iframe document');
      log('  Document readyState:', doc.readyState);
      return doc;
    } else {
      log('❌ Could not access iframe document (contentDocument is null)');
    }
  } catch (e) {
    log('❌ Error accessing iframe document (cross-origin?):', e.message);
  }

  return null;
}

// ============================================================================
// EVENT HANDLERS
// ============================================================================

function getFreshIframeDoc() {
  // Delegates to getIframeDoc() which always returns a fresh reference.
  return getIframeDoc();
}

// ============================================================================
// RANGE-RESTART CONFIRMATION MODAL (v4.5)
// Shown when a customer who already has a COMPLETED selection clicks another
// date. The click has been blocked before reaching BTA, so the calendar still
// shows the existing selection. Resolves 'restart' or 'keep'.
// ============================================================================
function showRangeRestartModal(clickedDateObj) {
  return new Promise((resolve) => {
    log('═══════════════════════════════════════════════════════════');
    log('Showing range-restart confirmation modal');

    const curDropDate = formatDateShort(timeState.dropOffDate);
    const curDropTime = timeState.dropOffTime || '';
    const curPickDate = formatDateShort(timeState.pickUpDate);
    const curPickTime = timeState.pickUpTime || '';
    const newDateStr = formatDateShort(clickedDateObj);

    const modalId = 'range-restart-modal-' + Date.now();
    const modalHTML = `
      <div id="${modalId}" style="
        position: fixed;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        background: rgba(0, 0, 0, 0.7);
        display: flex;
        align-items: center;
        justify-content: center;
        z-index: 999999;
        padding: 20px;
        box-sizing: border-box;
        animation: fadeIn 0.25s ease-out;
      ">
        <div style="
          background: white;
          border: 3px solid #BD9B60;
          border-radius: 12px;
          padding: 0;
          max-width: 440px;
          width: 100%;
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
          overflow: hidden;
          animation: slideDown 0.25s ease-out;
        ">
          <div style="
            background: linear-gradient(135deg, #BD9B60 0%, #a37f4a 100%);
            padding: 18px 24px;
          ">
            <div style="font-size: 19px; font-weight: 700; color: white;">
              Change Your Dates?
            </div>
          </div>
          <div style="padding: 22px 24px 24px;">
            <div style="
              background: #f9fafb;
              border: 2px solid #e5e7eb;
              border-radius: 8px;
              padding: 14px 16px;
              margin-bottom: 16px;
            ">
              <div style="font-size: 13px; color: #6b7280; font-weight: 600; margin-bottom: 6px;">
                Your current selection
              </div>
              <div style="font-size: 15px; color: #1f2937; line-height: 1.6;">
                <strong>Drop-off:</strong> ${curDropDate} (${curDropTime})<br>
                <strong>Pick-up:</strong> ${curPickDate} (${curPickTime})
              </div>
            </div>
            <p style="
              margin: 0 0 20px 0;
              color: #374151;
              font-size: 15px;
              line-height: 1.55;
            ">
              Would you like to start over with <strong>${newDateStr}</strong> as your new drop-off date? You'll be asked to choose your dates and times again.
            </p>
            <div style="display: flex; flex-direction: column; gap: 10px;">
              <button id="range-restart-yes" style="
                background: #BD9B60;
                color: white;
                border: none;
                padding: 14px 24px;
                border-radius: 8px;
                font-size: 15px;
                font-weight: 700;
                cursor: pointer;
                width: 100%;
                transition: background 0.18s;
              ">Start Over With New Dates</button>
              <button id="range-restart-keep" style="
                background: white;
                color: #6b7280;
                border: 2px solid #d1d5db;
                padding: 12px 24px;
                border-radius: 8px;
                font-size: 14px;
                font-weight: 600;
                cursor: pointer;
                width: 100%;
                transition: all 0.18s;
              ">Keep My Current Selection</button>
            </div>
          </div>
        </div>
      </div>
      <style>
        #range-restart-yes:hover { background: #a37f4a !important; }
        #range-restart-keep:hover { border-color: #9ca3af !important; color: #374151 !important; background: #f9fafb !important; }
      </style>
    `;

    const container = document.createElement('div');
    container.innerHTML = modalHTML;
    document.body.appendChild(container);
    state.modalActive = true;
    document.body.style.overflow = 'hidden';

    const modal = document.getElementById(modalId);

    const closeWith = (decision) => {
      modal.style.animation = 'fadeOut 0.2s ease-out';
      setTimeout(() => {
        container.remove();
        state.modalActive = false;
        document.body.style.overflow = 'auto';
        log(`Range-restart modal closed — decision: ${decision}`);
        log('═══════════════════════════════════════════════════════════');
        // Small delay so the modal is fully gone before any re-fired click
        setTimeout(() => resolve(decision), 50);
      }, 200);
    };

    document.getElementById('range-restart-yes').addEventListener('click', (e) => {
      e.stopPropagation();
      closeWith('restart');
    });
    document.getElementById('range-restart-keep').addEventListener('click', (e) => {
      e.stopPropagation();
      closeWith('keep');
    });
    // Backdrop click and Escape both mean "keep" — the safe, no-change default
    modal.addEventListener('click', (e) => {
      if (e.target === modal) closeWith('keep');
    });
    const escapeHandler = (e) => {
      if (e.key === 'Escape' && state.modalActive) {
        document.removeEventListener('keydown', escapeHandler);
        closeWith('keep');
      }
    };
    document.addEventListener('keydown', escapeHandler);
  });
}

// ============================================================================
// BTA SELECTION VERIFICATION (v4.7)
// The script counts taps; BTA keeps its own range. These helpers read BTA's
// real selection from the calendar after each tap and repair or reset when
// the two disagree. BTA behaviour (verified live 2026-10-28):
//   - nothing selected + tap X          -> start-only at X (range_start+range_end on X)
//   - start-only X + tap later Y        -> range X..Y
//   - start-only X + tap earlier W      -> start-only at W
//   - start-only X + tap X again        -> range X..X+1 (!)
//   - complete range + tap any date Z   -> start-only at Z
// ============================================================================

function kcbSameDate(a, b) {
  return !!(a && b && a.year === b.year && a.monthIndex === b.monthIndex && a.day === b.day);
}

function kcbDateKey(o) {
  return o ? `${o.year}-${o.monthIndex + 1}-${o.day}` : null;
}

// Find the calendar button for a date in the CURRENTLY displayed month view
// (null when that date isn't on screen). Prefers a non-outside day.
function kcbFindDayButton(doc, dateObj) {
  if (!doc || !dateObj) return null;
  let fallback = null;
  const buttons = doc.querySelectorAll('button.rdp-day');
  for (const btn of buttons) {
    if (parseInt(btn.textContent.trim(), 10) !== dateObj.day) continue;
    const parsed = parseDateFromButton(btn, doc);
    if (!kcbSameDate(parsed, dateObj)) continue;
    if (!btn.classList.contains('rdp-day_outside')) return btn;
    if (!fallback) fallback = btn;
  }
  return fallback;
}

// 'single' = start-only (or 1-day) | 'start' | 'end' | 'middle' | 'none' | 'missing'
function kcbDayState(btn) {
  if (!btn) return 'missing';
  const c = btn.classList;
  const s = c.contains('rdp-day_range_start');
  const e = c.contains('rdp-day_range_end');
  if (s && e) return 'single';
  if (s) return 'start';
  if (e) return 'end';
  if (c.contains('rdp-day_range_middle')) return 'middle';
  if (c.contains('rdp-day_selected') || btn.getAttribute('aria-selected') === 'true') return 'selected';
  return 'none';
}

// Tap a date on the customer's behalf without the script counting it
function kcbInternalTap(btn) {
  if (!btn || !btn.isConnected) return false;
  state.internalTap = true;
  try {
    btn.click();
  } catch (err) {
    log('⚠ Internal tap failed:', err);
    return false;
  } finally {
    state.internalTap = false;
  }
  return true;
}

// After a DROP-OFF tap BTA must show that date as start-only ('single').
function scheduleBtaDropOffCheck(dateObj) {
  const token = ++state.verifyToken;
  setTimeout(() => verifyBtaDropOff(dateObj, token, 1, 0), 250);
}

function verifyBtaDropOff(dateObj, token, attempt, repairs) {
  try {
    if (token !== state.verifyToken) return;               // a newer tap took over
    if (!kcbSameDate(state.firstSelectedDate, dateObj)) return; // no longer our drop-off
    const doc = getIframeDoc();
    const btn = kcbFindDayButton(doc, dateObj);
    const st = kcbDayState(btn);

    if (st === 'single') {
      if (repairs > 0) {
        telNotable('bta_repaired', { phase: 'dropoff', date: kcbDateKey(dateObj), repairs: repairs });
        log('✓ BTA selection repaired - drop-off now matches');
      } else {
        telEvent('btaCheck', { phase: 'dropoff', ok: true });
      }
      return;
    }
    if (st === 'missing') {
      telEvent('btaCheck', { phase: 'dropoff', ok: null, reason: 'not_visible' });
      return; // can't see it (month changed) - Next-button check covers this
    }
    if (st === 'none' && attempt === 1 && repairs === 0) {
      // BTA may just be slow to re-render - look again before acting
      setTimeout(() => verifyBtaDropOff(dateObj, token, 2, repairs), 450);
      return;
    }

    telNotable('bta_desync', { phase: 'dropoff', date: kcbDateKey(dateObj), btaState: st, repairs: repairs });
    log(`⚠ BTA desync on drop-off ${formatDateShort(dateObj)}: BTA state "${st}"`);

    if (repairs < 2 && kcbInternalTap(btn)) {
      setTimeout(() => verifyBtaDropOff(dateObj, token, 2, repairs + 1), 300);
      return;
    }
    showCalendarResyncModal('dropoff', st);
  } catch (err) {
    log('verifyBtaDropOff error:', err);
  }
}

// After a PICK-UP tap BTA must show pick-up as range END and, when visible,
// drop-off as range START.
function scheduleBtaPickUpCheck(dropDate, pickDate) {
  const token = ++state.verifyToken;
  setTimeout(() => verifyBtaPickUp(dropDate, pickDate, token, 1, false), 250);
}

function verifyBtaPickUp(dropDate, pickDate, token, attempt, repaired) {
  try {
    if (token !== state.verifyToken) return;
    if (!dropDate || !pickDate) return;
    const doc = getIframeDoc();
    const pickBtn = kcbFindDayButton(doc, pickDate);
    const dropBtn = kcbFindDayButton(doc, dropDate);
    const pickSt = kcbDayState(pickBtn);
    const dropSt = kcbDayState(dropBtn);
    const pickOk = pickSt === 'end';
    const dropOk = dropSt === 'missing' || dropSt === 'start';

    if (pickOk && dropOk) {
      if (repaired) {
        telNotable('bta_repaired', { phase: 'pickup', dropOff: kcbDateKey(dropDate), pickUp: kcbDateKey(pickDate) });
      } else {
        telEvent('btaCheck', { phase: 'pickup', ok: true });
      }
      return;
    }
    if (pickSt === 'missing') {
      telEvent('btaCheck', { phase: 'pickup', ok: null, reason: 'not_visible' });
      return;
    }
    if (pickSt === 'none' && attempt === 1 && !repaired) {
      setTimeout(() => verifyBtaPickUp(dropDate, pickDate, token, 2, repaired), 450);
      return;
    }

    telNotable('bta_desync', {
      phase: 'pickup', dropOff: kcbDateKey(dropDate), pickUp: kcbDateKey(pickDate),
      pickState: pickSt, dropState: dropSt, repaired: repaired
    });
    log(`⚠ BTA desync on pick-up: pick "${pickSt}", drop "${dropSt}"`);

    // Repair is only possible when both dates are on screen and in order:
    // tap drop-off (BTA restarts there), then pick-up (BTA completes range)
    const ordered = new Date(dropDate.year, dropDate.monthIndex, dropDate.day) <
                    new Date(pickDate.year, pickDate.monthIndex, pickDate.day);
    if (!repaired && dropBtn && pickBtn && ordered) {
      kcbInternalTap(dropBtn);
      setTimeout(() => {
        const freshDoc = getIframeDoc();
        kcbInternalTap(kcbFindDayButton(freshDoc, pickDate));
        setTimeout(() => verifyBtaPickUp(dropDate, pickDate, token, 2, true), 300);
      }, 300);
      return;
    }
    showCalendarResyncModal('pickup', pickSt);
  } catch (err) {
    log('verifyBtaPickUp error:', err);
  }
}

// Final check when Next is clicked: whatever part of the stay is visible in
// the calendar must match the script's dates. Returns true when OK (or when
// nothing relevant is visible - the per-tap checks already covered it).
function verifyBtaBeforeNext() {
  try {
    const drop = timeState.dropOffDate;
    const pick = timeState.pickUpDate;
    if (!drop || !pick) return true;
    const doc = getIframeDoc();
    if (!doc) return true;
    const dropBtn = kcbFindDayButton(doc, drop);
    const pickBtn = kcbFindDayButton(doc, pick);
    const dropSt = kcbDayState(dropBtn);
    const pickSt = kcbDayState(pickBtn);
    const problems = [];
    if (dropSt !== 'missing' && dropSt !== 'start') problems.push('drop:' + dropSt);
    if (pickSt !== 'missing' && pickSt !== 'end') problems.push('pick:' + pickSt);

    // No BTA-selected day on screen may fall outside the stay
    const dropT = new Date(drop.year, drop.monthIndex, drop.day).getTime();
    const pickT = new Date(pick.year, pick.monthIndex, pick.day).getTime();
    doc.querySelectorAll('button.rdp-day').forEach((btn) => {
      const st = kcbDayState(btn);
      if (st === 'none') return;
      const d = parseDateFromButton(btn, doc);
      if (!d) return;
      const t = new Date(d.year, d.monthIndex, d.day).getTime();
      if (t < dropT || t > pickT) problems.push('extra:' + kcbDateKey(d) + ':' + st);
    });

    if (problems.length === 0) {
      telEvent('btaCheck', { phase: 'next', ok: true, visible: (dropSt !== 'missing') + '/' + (pickSt !== 'missing') });
      return true;
    }
    telNotable('next_check_failed', {
      dropOff: kcbDateKey(drop), pickUp: kcbDateKey(pick), problems: problems.slice(0, 10)
    });
    return false;
  } catch (err) {
    log('verifyBtaBeforeNext error:', err);
    return true; // never block a booking because the check itself failed
  }
}

// Shown when BTA's selection can't be brought back in line. The only safe
// action is a clean calendar, so there is a single button.
function showCalendarResyncModal(phase, btaState) {
  try {
    if (document.getElementById('kcb-resync-modal')) return;
    telNotable('bta_resync_modal', { phase: phase, btaState: btaState || null });
    const container = document.createElement('div');
    container.innerHTML = `
      <div id="kcb-resync-modal" style="
        position: fixed; inset: 0; background: rgba(0,0,0,0.7);
        display: flex; align-items: center; justify-content: center;
        z-index: 99999999; padding: 20px; box-sizing: border-box;
        animation: fadeIn 0.25s ease-out;
      ">
        <div style="
          background: white; border: 3px solid #BD9B60; border-radius: 12px;
          max-width: 440px; width: 100%; overflow: hidden;
          box-shadow: 0 8px 32px rgba(0,0,0,0.3);
          font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        ">
          <div style="background: linear-gradient(135deg, #BD9B60 0%, #a37f4a 100%); padding: 18px 24px;">
            <div style="font-size: 19px; font-weight: 700; color: white;">Let's Re-Select Your Dates</div>
          </div>
          <div style="padding: 22px 24px 24px;">
            <p style="margin: 0 0 14px; color: #374151; font-size: 15px; line-height: 1.55;">
              The calendar didn't register one of your taps, so the dates it's holding don't match the dates you picked.
            </p>
            <p style="margin: 0 0 20px; color: #374151; font-size: 15px; line-height: 1.55;">
              To make sure you're booked for the right dates, click <strong>"Clear and Re-Select Dates"</strong> below and choose your drop-off and pick-up dates again.
            </p>
            <button id="kcb-resync-btn" style="
              background: #BD9B60; color: white; border: none; padding: 14px 24px;
              border-radius: 8px; font-size: 15px; font-weight: 700; cursor: pointer; width: 100%;
            ">Clear and Re-Select Dates</button>
          </div>
        </div>
      </div>`;
    document.body.appendChild(container);
    state.modalActive = true;
    document.body.style.overflow = 'hidden';
    document.getElementById('kcb-resync-btn').addEventListener('click', (e) => {
      e.stopPropagation();
      telSend('resync_reload');
      resetBookingSelection(); // page reload - the only reliable way to clear BTA
    });
  } catch (err) {
    log('showCalendarResyncModal error:', err);
  }
}

function handleDateClick(event, button) {
  log('═══════════════════════════════════════════════════════════');
  log('handleDateClick() TRIGGERED');
  log('═══════════════════════════════════════════════════════════');
  log('Event object:', event);
  log('Button element:', button);
  log('Event type:', event.type);
  log('Event target:', event.target);
  log('Current click count:', state.clickCount);

  const doc = getIframeDoc();
  if (!doc) {
    log('❌ No iframe document available - BTA widget may not be ready');
    return;
  }

  const dateObj = parseDateFromButton(button, doc);
  if (!dateObj) {
    log('❌ Could not parse date from button');
    return;
  }

  log(`✓ Date clicked: ${formatDateShort(dateObj)}`);
  log(`  Click count: ${state.clickCount}`);
  log(`  Modal active: ${state.modalActive}`);

  // v4.3 telemetry
  telEvent('dateClick', {
    parsed: `${dateObj.year}-${dateObj.monthIndex + 1}-${dateObj.day}`,
    clickCountBefore: state.clickCount
  });

  // ── v4.5 RANGE-RESTART GUARD ───────────────────────────────────────────
  // A click that would start a NEW selection while a COMPLETED selection
  // exists would make BTA silently restart its range ("my dates
  // disappeared"). Block it before BTA sees it, keep the calendar intact,
  // and ask the customer what they want.
  const hasCompletedSelection = !!(timeState.dropOffDate && timeState.dropOffTime &&
                                   timeState.pickUpDate && timeState.pickUpTime);
  if (state.clickCount === 0 && hasCompletedSelection && !state.bypassRestartGuard) {
    log('🛑 Range-restart guard: blocking click, asking customer');
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();
    telEvent('restart_guard_shown', {
      clicked: `${dateObj.year}-${dateObj.monthIndex + 1}-${dateObj.day}`
    });
    setTimeout(() => {
      showRangeRestartModal(dateObj).then((decision) => {
        if (decision === 'restart') {
          telNotable('range_restart', {
            newDropOff: `${dateObj.year}-${dateObj.monthIndex + 1}-${dateObj.day}`,
            stalePickUp: `${timeState.pickUpDate.year}-${timeState.pickUpDate.monthIndex + 1}-${timeState.pickUpDate.day}`,
            via: 'modal'
          });
          resetTimeSelections();
          removeCascadeDisable(getIframeDoc());
          state.clickCount = 0;
          state.firstSelectedDate = null;
          // One-shot bypass: re-fire the click so BTA restarts its range and
          // the normal first-click flow (drop-off time modal) follows
          state.bypassRestartGuard = true;
          const freshBtn = button.isConnected ? button : null;
          if (freshBtn) {
            freshBtn.click();
          } else {
            log('⚠ Range-restart: original button no longer in DOM — customer can re-click manually');
            state.bypassRestartGuard = false;
          }
        } else {
          telEvent('restart_guard_keep', null);
          log('✓ Customer kept current selection — no changes');
        }
      });
    }, 50);
    return;
  }
  if (state.bypassRestartGuard) {
    state.bypassRestartGuard = false;
    log('Range-restart guard bypassed (customer-confirmed restart)');
  }
  // ── END v4.5 RANGE-RESTART GUARD ───────────────────────────────────────

  // If this is a pick-up selection (click count = 1), check for sold-out dates between drop-off and this date
  if (state.clickCount === 1 && state.firstSelectedDate) {
    log('  Checking for sold-out dates between drop-off and pick-up...');

    // Find any sold-out dates between drop-off and the clicked date
    var soldOutDates = findSoldOutDatesBetweenDynamic(state.firstSelectedDate, dateObj, doc);

    if (soldOutDates.length > 0) {
      log('🚫 Found sold-out dates in range - blocking pick-up selection');
      log('  Sold-out dates:', soldOutDates.map(function(d) { return formatDateShort(d); }).join(', '));

      // Prevent the click
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation();

      // Show the spanning modal
      setTimeout(function() {
        showSoldOutSpanningModal(state.firstSelectedDate, dateObj, soldOutDates);
      }, 100);

      return;
    }
  }

  const restriction = isRestrictedDate(dateObj);

  if (!restriction) {
    log('✗ Not a restricted date - allowing normal flow');

    state.clickCount++;

    if (state.clickCount === 1) {
      state.firstSelectedDate = dateObj;
      log('✓ First date (drop-off) stored:', state.firstSelectedDate);

      // v4.4 FIX A: a new first click while a completed prior selection
      // exists means BTA has restarted its range — clear our stale
      // timeState to match, so the missing-pickup modal fires correctly
      // if the user proceeds without re-selecting a pick-up date.
      if (timeState.pickUpDate || timeState.pickUpTime) {
        log('⚠ Range restart detected - clearing stale prior selection to match BTA');
        telNotable('range_restart', {
          newDropOff: `${dateObj.year}-${dateObj.monthIndex + 1}-${dateObj.day}`,
          stalePickUp: timeState.pickUpDate
            ? `${timeState.pickUpDate.year}-${timeState.pickUpDate.monthIndex + 1}-${timeState.pickUpDate.day}`
            : null
        });
        resetTimeSelections();
      }

      // Check if next day is sold out before showing time selection
      if (isNextDaySoldOut(dateObj, doc)) {
        log('⚠ Next day is sold out - showing sold-out modal instead');
        // v4.7: block the click so BTA doesn't keep a start date the
        // script has just discarded (previously caused a hidden desync)
        event.preventDefault();
        event.stopPropagation();
        event.stopImmediatePropagation();
        state.clickCount = 0;  // Reset since this date won't be selected
        state.firstSelectedDate = null;
        setTimeout(() => {
          showSoldOutModal(dateObj);
        }, 100);
        return;
      }

      // Apply cascade disable immediately for visual feedback
      applyCascadeDisable(dateObj, doc);

      setTimeout(() => {
        showTimeSelectionModal(dateObj, false).then(selectedPeriod => {
          log(`✓ Drop-off time selected: ${selectedPeriod}`);
        });
      }, 100);

      // v4.7: confirm BTA really started its range on this date
      scheduleBtaDropOffCheck(dateObj);

    } else if (state.clickCount === 2) {
      log('✓ Second date (pickup) selected');

      // Remove cascade disable since booking is progressing
      removeCascadeDisable(doc);

      // v4.7: confirm BTA's range really runs drop-off -> this date
      scheduleBtaPickUpCheck(state.firstSelectedDate, dateObj);

      setTimeout(() => {
        showTimeSelectionModal(dateObj, true).then(selectedPeriod => {
          log(`✓ Pick-up time selected: ${selectedPeriod}`);
        });
      }, 100);

      setTimeout(() => {
        state.clickCount = 0;
        state.firstSelectedDate = null;
        log('✓ Click count reset');
      }, 500);

    } else if (state.clickCount >= 3) {
      log('✓ Click count >= 3, resetting selections');

      // Remove old cascade disable
      removeCascadeDisable(doc);

      state.clickCount = 1;
      state.firstSelectedDate = dateObj;
      resetTimeSelections();

      // Check if next day is sold out before showing time selection
      if (isNextDaySoldOut(dateObj, doc)) {
        log('⚠ Next day is sold out - showing sold-out modal instead');
        state.clickCount = 0;  // Reset since this date won't be selected
        state.firstSelectedDate = null;
        setTimeout(() => {
          showSoldOutModal(dateObj);
        }, 100);
        return;
      }

      // Apply new cascade disable immediately
      applyCascadeDisable(dateObj, doc);

      setTimeout(() => {
        showTimeSelectionModal(dateObj, false).then(selectedPeriod => {
          log(`✓ New drop-off time selected: ${selectedPeriod}`);
        });
      }, 100);

      // v4.7: confirm BTA really started its range on this date
      scheduleBtaDropOffCheck(dateObj);
    }

    return;
  }

  if (restriction.type === 'AM_ONLY') {
    log('  Dec 24th detected - allowing selection, will show info modal after');

    state.clickCount++;

    if (state.clickCount === 1) {
      state.firstSelectedDate = dateObj;
      // v4.4 FIX A: clear any stale completed prior selection (BTA range
      // restart) before storing the forced AM drop-off below
      if (timeState.pickUpDate || timeState.pickUpTime) {
        log('⚠ Range restart detected (Dec 24 branch) - clearing stale prior selection');
        telNotable('range_restart', {
          newDropOff: `${dateObj.year}-${dateObj.monthIndex + 1}-${dateObj.day}`,
          stalePickUp: timeState.pickUpDate
            ? `${timeState.pickUpDate.year}-${timeState.pickUpDate.monthIndex + 1}-${timeState.pickUpDate.day}`
            : null,
          branch: 'dec24'
        });
        resetTimeSelections();
      }
      log('✓ First date (drop-off) stored:', state.firstSelectedDate);

      // Dec 24 is AM-only: there is no AM/PM choice to make, so populate
      // timeState with the forced AM drop-off. Without this the Next handler
      // sees no drop-off time and skips the Flex Booking Protection modal.
      timeState.dropOffTime = 'AM';
      timeState.dropOffDate = dateObj;

      // Apply cascade disable immediately
      applyCascadeDisable(dateObj, doc);

      // v4.7: confirm BTA really started its range on this date
      scheduleBtaDropOffCheck(dateObj);

    } else if (state.clickCount === 2) {
      log('✓ Second date (pickup) selected');

      // Dec 24 is AM-only: populate timeState with the forced AM pick-up so
      // the Next handler sees a complete selection (otherwise it triggers the
      // 'pick-up date required' modal and blocks checkout).
      timeState.pickUpTime = 'AM';
      timeState.pickUpDate = dateObj;

      // Remove cascade disable
      removeCascadeDisable(doc);

      // v4.7: confirm BTA's range really runs drop-off -> this date
      scheduleBtaPickUpCheck(state.firstSelectedDate, dateObj);

      setTimeout(() => {
        state.clickCount = 0;
        state.firstSelectedDate = null;
        log('✓ Click count reset');
      }, 500);
    }

    const isDropOff = state.clickCount === 1;

    setTimeout(() => {
      const modalData = generateDec24InfoModal(dateObj, isDropOff);
      showDec24InfoModalNonBlocking(modalData, dateObj, isDropOff);
      log('✓ Dec 24th info modal shown (non-blocking)');
    }, 100);

    log('═══════════════════════════════════════════════════════════');
    return;
  }

  log('🚫 RESTRICTED DATE DETECTED!');
  log('  Restriction type:', restriction.type);
  log('  Preventing default event behavior...');

  event.preventDefault();
  event.stopPropagation();
  event.stopImmediatePropagation();

  log('✓ Event prevented successfully');

  const isDropOff = state.clickCount === 0;
  log(`  This is a ${isDropOff ? 'DROP-OFF' : 'PICKUP'} date`);

  log('  Finding alternative dates...');
  const alternatives = findAlternativeDates(dateObj);
  log('  Alternatives:', alternatives);

  log('  Generating modal content...');
  const modalData = generateModalContent(restriction, dateObj, isDropOff, alternatives, state.firstSelectedDate);
  log('  Modal data generated:', { modalId: modalData.modalId });

  log('  Showing modal...');
  showModal(modalData, dateObj, isDropOff);
  log('✓ Modal shown');
  log('═══════════════════════════════════════════════════════════');
}

function injectAvailabilityBadge(doc) {
  if (!doc) return;

  // Skip if already injected
  if (doc.getElementById('bta-availability-badge')) return;

  // Find the price element — try multiple selectors
  let priceElement = findPriceElement(doc);
  if (!priceElement) {
    const allSpans = doc.querySelectorAll('span');
    for (const span of allSpans) {
      if (span.textContent.includes('$') && span.textContent.match(/\d/)) {
        const prevText = span.previousSibling?.textContent || span.parentElement?.textContent || '';
        if (prevText.toLowerCase().includes('price')) {
          priceElement = span;
          break;
        }
      }
    }
  }
  if (!priceElement) return;

  // Walk up to the price row/wrapper
  let priceContainer = priceElement.parentElement;

  // Create the badge
  const badge = doc.createElement('div');
  badge.id = 'bta-availability-badge';
  badge.innerHTML = `
    <span style="display:flex;align-items:center;justify-content:center;gap:8px;">
      <span style="color:#22c55e;font-weight:bold;">✓</span>
      <span>Real-time availability loaded</span>
      <span id="bta-badge-info-icon" style="
        display:inline-flex;align-items:center;justify-content:center;
        width:15px;height:15px;border-radius:50%;
        background:transparent;border:1.5px solid #BD9B60;color:#BD9B60;
        font-size:10px;font-weight:700;font-style:normal;
        cursor:pointer;flex-shrink:0;user-select:none;
        font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',Roboto,sans-serif;
        transition:border-color 0.15s ease,color 0.15s ease;
      ">i</span>
    </span>
  `;
  badge.style.cssText = `
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    margin: 0 0 12px 0;
    padding: 10px 14px;
    border-radius: 6px;
    text-align: center;
    font-size: 14px;
    line-height: 1.4;
    background: #222222;
    border: 1px solid #BD9B60;
    color: #EAEAEA;
    cursor: default;
    position: relative;
  `;

  // Inject badge + tooltip styles if not already present
  if (!doc.getElementById('bta-badge-styles')) {
    const style = doc.createElement('style');
    style.id = 'bta-badge-styles';
    style.textContent = `
      @media (max-width: 480px) {
        #bta-availability-badge {
          font-size: 12px !important;
          padding: 8px 10px !important;
          margin-bottom: 8px !important;
        }
      }
      #bta-availability-tooltip {
        position: fixed;
        background: #2e2e2e;
        color: #EAEAEA;
        border: 1px solid #BD9B60;
        border-radius: 6px;
        padding: 10px 14px;
        font-size: 13px;
        line-height: 1.5;
        max-width: 260px;
        text-align: center;
        z-index: 99999;
        pointer-events: none;
        opacity: 0;
        transition: opacity 0.2s ease;
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        box-shadow: 0 4px 12px rgba(0,0,0,0.4);
      }
      #bta-availability-tooltip.visible {
        opacity: 1;
      }
      #bta-availability-tooltip::after {
        content: '';
        position: absolute;
        top: 100%;
        left: 50%;
        transform: translateX(-50%);
        border: 6px solid transparent;
        border-top-color: #BD9B60;
      }
    `;
    doc.head.appendChild(style);
  }

  // Create the tooltip element
  const tooltip = doc.createElement('div');
  tooltip.id = 'bta-availability-tooltip';
  tooltip.textContent = 'Live availability data successfully loaded.';


  // Position tooltip centered above the badge
  function positionTooltip() {
    const rect = badge.getBoundingClientRect();
    const iframeWidth = doc.documentElement.clientWidth || doc.body.clientWidth;
    const tooltipWidth = 260;

    let left = rect.left + rect.width / 2;
    // Clamp to stay within viewport
    if (left - tooltipWidth / 2 < 8) left = tooltipWidth / 2 + 8;
    if (left + tooltipWidth / 2 > iframeWidth - 8) left = iframeWidth - tooltipWidth / 2 - 8;

    tooltip.style.left = left + 'px';
    tooltip.style.top = (rect.top - 10) + 'px';
    tooltip.style.transform = 'translate(-50%, -100%)';
  }

  const isMobile = 'ontouchstart' in window;
  let mobileTooltipVisible = false;
  let mobileDismissTimeout = null;

  // Use the ⓘ icon as the trigger if available, otherwise fall back to the whole badge
  function getInfoIcon() {
    return doc.getElementById('bta-badge-info-icon') || badge;
  }

  if (isMobile) {
    // Mobile: tap ⓘ to toggle, auto-dismiss after 5s, tap outside to dismiss
    badge.addEventListener('click', (e) => {
      const infoIcon = getInfoIcon();
      // Only trigger if the ⓘ icon was tapped (or the whole badge if no icon)
      if (e.target !== infoIcon && infoIcon !== badge) return;
      e.stopPropagation();
      if (mobileTooltipVisible) {
        tooltip.classList.remove('visible');
        mobileTooltipVisible = false;
        clearTimeout(mobileDismissTimeout);
      } else {
        positionTooltip();
        tooltip.classList.add('visible');
        mobileTooltipVisible = true;
        mobileDismissTimeout = setTimeout(() => {
          tooltip.classList.remove('visible');
          mobileTooltipVisible = false;
        }, 5000);
      }
    });

    doc.addEventListener('click', (e) => {
      if (mobileTooltipVisible && !badge.contains(e.target)) {
        tooltip.classList.remove('visible');
        mobileTooltipVisible = false;
        clearTimeout(mobileDismissTimeout);
      }
    });
  } else {
    // Desktop: hover ⓘ icon to show, mouse out to hide
    const infoIcon = getInfoIcon();
    infoIcon.addEventListener('mouseenter', () => {
      positionTooltip();
      tooltip.classList.add('visible');
    });
    infoIcon.addEventListener('mouseleave', () => {
      tooltip.classList.remove('visible');
    });
    // Also show on full badge hover for discoverability
    badge.addEventListener('mouseenter', () => {
      positionTooltip();
      tooltip.classList.add('visible');
    });
    badge.addEventListener('mouseleave', () => {
      tooltip.classList.remove('visible');
    });
  }

  // Insert before the price container
  if (priceContainer && priceContainer.parentElement) {
    priceContainer.parentElement.insertBefore(badge, priceContainer);
    doc.body.appendChild(tooltip);
  }
}

// ============================================================================
// WAITLIST BUTTON - Always visible inside the BTA widget
// ============================================================================
/**
 * Injects an outlined "Dates Unavailable? Join Waitlist" button at the bottom
 * of the BTA widget (after the price container). Idempotent.
 */
function injectWaitlistButton(doc) {
  if (!doc) return;

  // Skip if already injected
  if (doc.getElementById('bta-waitlist-button')) return;

  // Find the price element using same multi-selector fallback as the badge
  let priceElement = findPriceElement(doc);
  if (!priceElement) {
    const allSpans = doc.querySelectorAll('span');
    for (const span of allSpans) {
      if (span.textContent.includes('$') && span.textContent.match(/\d/)) {
        const prevText = span.previousSibling?.textContent || span.parentElement?.textContent || '';
        if (prevText.toLowerCase().includes('price')) {
          priceElement = span;
          break;
        }
      }
    }
  }
  if (!priceElement) return;

  // Walk up to the price row/wrapper (same logic as the badge)
  const priceContainer = priceElement.parentElement;
  if (!priceContainer || !priceContainer.parentElement) return;

  // Build the button as an anchor (opens new tab, no JS needed)
  const button = doc.createElement('a');
  button.id = 'bta-waitlist-button';
  button.href = CONFIG.waitlistUrl;
  button.target = '_blank';
  button.rel = 'noopener noreferrer';
  button.textContent = 'Dates Unavailable? Join Waitlist';
  button.style.cssText = `
    display: block;
    box-sizing: border-box;
    width: 100%;
    margin: 14px 0 4px 0;
    padding: 10px 14px;
    border: 1.5px solid #BD9B60;
    border-radius: 6px;
    background: transparent;
    color: #BD9B60;
    text-align: center;
    text-decoration: none;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    font-size: 14px;
    font-weight: 600;
    line-height: 1.4;
    cursor: pointer;
    transition: background 0.15s ease, color 0.15s ease;
  `;

  // Inject hover/responsive styles once per document
  if (!doc.getElementById('bta-waitlist-button-styles')) {
    const style = doc.createElement('style');
    style.id = 'bta-waitlist-button-styles';
    style.textContent = `
      #bta-waitlist-button:hover,
      #bta-waitlist-button:focus {
        background: #BD9B60 !important;
        color: #222222 !important;
        outline: none;
      }
      @media (max-width: 480px) {
        #bta-waitlist-button {
          font-size: 12px !important;
          padding: 8px 10px !important;
          margin-top: 10px !important;
        }
      }
    `;
    doc.head.appendChild(style);
  }

  // Insert AFTER the price container (i.e. at the very bottom of the widget)
  priceContainer.parentElement.insertBefore(button, priceContainer.nextSibling);
}

// ============================================================================
// MONTH JUMP DROPDOWN
// Replaces the react-day-picker month label with a native <select> so users
// can jump straight to any of the next 12 months instead of clicking the
// arrows one month at a time. Purely additive; the original label is hidden
// (not removed) so getCurrentMonth() can still read the displayed month.
// ============================================================================

/**
 * Build the 12 dropdown entries: the current real month + the next 11 months.
 * Boarding window is 365 days, so +11 months is always within range and the
 * calendar never needs earlier (past) entries.
 */
function getMonthDropdownOptions() {
  const options = [];
  const now = new Date();
  let y = now.getFullYear();
  let m = now.getMonth(); // 0-11
  for (let i = 0; i < 12; i++) {
    options.push({
      monthIndex: m,
      year: y,
      label: getMonthName(m) + ' ' + y
    });
    m++;
    if (m > 11) { m = 0; y++; }
  }
  return options;
}

/**
 * Set the dropdown's selected value to match the month the calendar is
 * currently displaying. Only changes the value if that month is one of our
 * 12 options (it always should be within the boarding window).
 */
function syncMonthDropdown(doc) {
  if (!doc) return;
  const select = doc.getElementById('bta-month-jump');
  if (!select) return;

  const current = getCurrentMonth(doc);
  if (!current) return;

  const monthIndex = getMonthIndex(current.month);
  const year = parseInt(current.year, 10);
  if (monthIndex === -1 || isNaN(year)) return;

  const value = year + '-' + monthIndex;
  let hasOption = false;
  for (let i = 0; i < select.options.length; i++) {
    if (select.options[i].value === value) { hasOption = true; break; }
  }
  if (hasOption && select.value !== value) {
    select.value = value;
  }
}

/**
 * Step the calendar's < / > arrows until the displayed month matches the
 * target. react-day-picker owns its month state internally, so clicking the
 * native nav buttons is the only way to move it from outside. We wait for the
 * caption to actually change between clicks (re-render is asynchronous) rather
 * than relying on fixed delays.
 */
function navigateCalendarToMonth(doc, targetMonthIndex, targetYear) {
  if (!doc) return;

  const targetOrdinal = targetYear * 12 + targetMonthIndex;
  const select = doc.getElementById('bta-month-jump');

  // Guard against overlapping navigations (e.g. rapid dropdown changes)
  if (doc._btaMonthNavigating) {
    log('navigateCalendarToMonth: navigation already in progress');
    return;
  }
  doc._btaMonthNavigating = true;
  if (select) select.disabled = true;

  const MAX_STEPS = 13;
  let steps = 0;

  function readState() {
    const cur = getCurrentMonth(doc);
    if (!cur) return null;
    const mi = getMonthIndex(cur.month);
    const y = parseInt(cur.year, 10);
    if (mi === -1 || isNaN(y)) return null;
    return { ordinal: y * 12 + mi, text: cur.month + ' ' + cur.year };
  }

  function finish() {
    doc._btaMonthNavigating = false;
    if (select) select.disabled = false;
    syncMonthDropdown(doc);
    // v4.3 telemetry: where did the navigation actually land
    const landed = getCurrentMonth(doc);
    telEvent('dropdownNavFinish', {
      steps: steps,
      landed: landed ? (landed.month + ' ' + landed.year) : 'unknown'
    });
  }

  function step() {
    const cur = readState();
    if (cur === null) { finish(); return; }
    if (cur.ordinal === targetOrdinal) { finish(); return; }
    if (steps >= MAX_STEPS) {
      log('navigateCalendarToMonth: hit MAX_STEPS without reaching target');
      finish();
      return;
    }
    steps++;

    const goForward = cur.ordinal < targetOrdinal;
    const btnName = goForward ? 'next-month' : 'previous-month';
    const btn = doc.querySelector('button[name="' + btnName + '"]');
    if (!btn || btn.disabled) {
      log('navigateCalendarToMonth: nav button missing or disabled (' + btnName + ')');
      finish();
      return;
    }

    const beforeText = cur.text;
    btn.click();

    // Poll for the caption to change, then continue stepping.
    let waited = 0;
    const POLL = 40;
    const MAX_WAIT = 1500;
    const poll = function() {
      if (!doc._btaMonthNavigating) return; // safety: aborted elsewhere
      const now = readState();
      if (now && now.text !== beforeText) {
        step();
        return;
      }
      waited += POLL;
      if (waited >= MAX_WAIT) {
        log('navigateCalendarToMonth: timed out waiting for month change');
        finish();
        return;
      }
      setTimeout(poll, POLL);
    };
    setTimeout(poll, POLL);
  }

  step();
}

/**
 * Watch the calendar for re-renders. If a re-render removes our dropdown, put
 * it back; otherwise keep the native label hidden and the selected value in
 * sync when the user navigates with the arrows. Attributes are intentionally
 * not observed, so hiding the label / setting the value never re-triggers this.
 */
function setupMonthDropdownObserver(doc) {
  if (!doc) return;
  if (doc._btaMonthDropdownObserver) return; // already observing this document

  const target = doc.querySelector('.rdp') || doc.querySelector('.rdp-caption');
  if (!target) return;

  const observer = new MutationObserver(function() {
    if (doc._btaMonthNavigating) return; // don't fight our own arrow-stepping

    const caption = doc.querySelector('.rdp-caption');
    if (!caption) return;

    const select = doc.getElementById('bta-month-jump');
    if (!select || !caption.contains(select)) {
      // Dropdown was wiped by a re-render — re-inject it.
      injectMonthDropdown(doc);
    } else {
      const label = caption.querySelector('.rdp-caption_label');
      if (label && label.style.display !== 'none') {
        label.style.display = 'none';
      }
      syncMonthDropdown(doc);
    }
  });

  observer.observe(target, { childList: true, subtree: true });
  doc._btaMonthDropdownObserver = observer;
  log('\u2713 Month dropdown observer set up');
}

/**
 * Inject the native <select> in place of the month label. Idempotent.
 */
function injectMonthDropdown(doc) {
  if (!doc) return;

  const caption = doc.querySelector('.rdp-caption');
  if (!caption) {
    log('injectMonthDropdown: no .rdp-caption found');
    return;
  }

  const label = caption.querySelector('.rdp-caption_label');

  // If the select already exists in this caption, just resync and ensure the
  // observer is running.
  let select = doc.getElementById('bta-month-jump');
  if (select && caption.contains(select)) {
    if (label && label.style.display !== 'none') label.style.display = 'none';
    syncMonthDropdown(doc);
    setupMonthDropdownObserver(doc);
    return;
  }

  // Inject styles once per document.
  if (!doc.getElementById('bta-month-jump-styles')) {
    const style = doc.createElement('style');
    style.id = 'bta-month-jump-styles';
    style.textContent = `
      #bta-month-jump {
        font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
        font-size: 15px;
        font-weight: 600;
        color: #FFFFFF;
        background-color: transparent;
        border: none;
        border-radius: 6px;
        padding: 6px 30px 6px 10px;
        margin: 0;
        cursor: pointer;
        line-height: 1.3;
        max-width: 100%;
        -webkit-appearance: none;
        -moz-appearance: none;
        appearance: none;
        background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 24 24' fill='none' stroke='%23BD9B60' stroke-width='3' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E");
        background-repeat: no-repeat;
        background-position: right 10px center;
      }
      #bta-month-jump:hover {
        background-color: #BD9B60;
      }
      #bta-month-jump option {
        background-color: #FFFFFF;
        color: #222222;
      }    
      #bta-month-jump:focus {
        outline: none;
        border-color: #BD9B60;
        box-shadow: 0 0 0 2px rgba(189,155,96,0.35);
      }
      #bta-month-jump:disabled {
        opacity: 0.6;
        cursor: default;
      }
      @media (max-width: 480px) {
        #bta-month-jump {
          font-size: 14px;
          padding: 5px 26px 5px 8px;
        }
      }
    `;
    doc.head.appendChild(style);
  }

  // Hide the native label but keep it in the DOM (getCurrentMonth reads it).
  if (label) label.style.display = 'none';

  // Build the select.
  select = doc.createElement('select');
  select.id = 'bta-month-jump';
  select.setAttribute('aria-label', 'Jump to month');

  const opts = getMonthDropdownOptions();
  for (let i = 0; i < opts.length; i++) {
    const o = opts[i];
    const optEl = doc.createElement('option');
    optEl.value = o.year + '-' + o.monthIndex;
    optEl.textContent = o.label;
    select.appendChild(optEl);
  }

  // Insert where the label sat: after the (hidden) label, before .rdp-nav.
  const nav = caption.querySelector('.rdp-nav');
  if (label && label.nextSibling) {
    caption.insertBefore(select, label.nextSibling);
  } else if (nav) {
    caption.insertBefore(select, nav);
  } else {
    caption.appendChild(select);
  }

  // Reflect the currently displayed month.
  syncMonthDropdown(doc);

  // Navigate on change.
  select.addEventListener('change', function() {
    const parts = select.value.split('-');
    const targetYear = parseInt(parts[0], 10);
    const targetMonthIndex = parseInt(parts[1], 10);
    if (isNaN(targetYear) || isNaN(targetMonthIndex)) return;
    // v4.3 telemetry: dropdown navigation is a suspect for the visible
    // "calendar jump" reports — capture the from/to months
    const curForTel = getCurrentMonth(doc);
    telNotable('dropdown_nav', {
      from: curForTel ? (curForTel.month + ' ' + curForTel.year) : 'unknown',
      to: getMonthName(targetMonthIndex) + ' ' + targetYear,
      midSelection: state.clickCount > 0 || !!timeState.dropOffDate
    });
    navigateCalendarToMonth(doc, targetMonthIndex, targetYear);
  });

  setupMonthDropdownObserver(doc);

  log('\u2713 Month jump dropdown injected');
}

function installEventListeners(doc) {
  log('═══════════════════════════════════════════════════════════');
  log('installEventListeners() called');
  log('═══════════════════════════════════════════════════════════');
  log('Document readyState:', doc.readyState);

  // Guard: remove any previously attached handler on this document before adding a new one.
  // This prevents stacking multiple click listeners when the watcher re-attaches.
  if (doc._kcbClickHandler) {
    doc.removeEventListener('click', doc._kcbClickHandler, true);
    log('Removed previous click listener before re-attaching');
  }

  const clickHandler = (e) => {
    log('───────────────────────────────────────────────────────────');
    log('CLICK EVENT DETECTED in iframe');
    log('  Event target:', e.target);
    log('  Target tagName:', e.target.tagName);
    log('  Target className:', e.target.className);
    log('  Target textContent:', e.target.textContent?.trim());
    log('  Modal active:', state.modalActive);

    // v4.7: our own repair taps must reach BTA but never be counted
    if (state.internalTap) {
      log('  Internal repair tap - not counted');
      return;
    }

    if (state.modalActive) {
      log('  Modal is active - ignoring click');
      return;
    }

    log('  Checking if target is or contains a date button...');
    const button = e.target.closest('button.rdp-day');

    if (button) {
      log('✓ DATE BUTTON FOUND!');
      log('  Button element:', button);
      log('  Button text:', button.textContent);
      log('  Button classes:', button.className);
      log('  Calling handleDateClick...');
      handleDateClick(e, button);
    } else {
      log('✗ Not a date button click');
      log('  Closest button.rdp-day:', button);
    }
    log('───────────────────────────────────────────────────────────');
  };

  // Store reference on the document so we can cleanly remove it on re-attach
  doc._kcbClickHandler = clickHandler;
  doc.addEventListener('click', clickHandler, true);

  log('✓ Click event listener installed (capture phase)');

  // v4.3 telemetry
  telEvent('listenersAttached', null);

  // Set up observer to re-cache sold-out dates when calendar month changes
  setupCalendarObserver(doc);

  // Inject real-time availability badge below calendar, above price
  injectAvailabilityBadge(doc);

  // Inject waitlist button at the bottom of the widget
  injectWaitlistButton(doc);

  // Replace the month label with a jump-to-month dropdown
  injectMonthDropdown(doc);

  log('═══════════════════════════════════════════════════════════');
}

/**
 * Set up MutationObserver to detect calendar month changes
 * and update sold-out date cache when needed
 */
function setupCalendarObserver(doc) {
  log('Setting up calendar observer for month changes');

  // Disconnect existing observer if any
  if (state.calendarObserver) {
    state.calendarObserver.disconnect();
  }

  var calendar = doc.querySelector('.rdp, [class*="calendar"], table');
  if (!calendar) {
    log('❌ No calendar element found for observer');
    return;
  }

  state.calendarObserver = new MutationObserver(function(mutations) {
    // Check if this looks like a month change (new date buttons added)
    var isMonthChange = false;

    for (var i = 0; i < mutations.length; i++) {
      var mutation = mutations[i];
      if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
        for (var j = 0; j < mutation.addedNodes.length; j++) {
          var node = mutation.addedNodes[j];
          if (node.nodeType === 1 && (node.classList && node.classList.contains('rdp-day') || (node.querySelector && node.querySelector('.rdp-day')))) {
            isMonthChange = true;
            break;
          }
        }
      }
      if (isMonthChange) break;
    }

    // If month changed and we have a drop-off selected, update the sold-out cache
    if (isMonthChange && state.firstSelectedDate && state.clickCount === 1) {
      log('📅 Calendar month changed - updating sold-out date cache');
      setTimeout(function() {
        // Just update the cache, no visual changes
        var soldOutDates = findSoldOutDatesAfterDropOff(state.firstSelectedDate, doc);
        state.soldOutDatesInRange = soldOutDates;
        log('  Updated cache: ' + soldOutDates.length + ' sold-out dates');
      }, 100);
    }
  });

  state.calendarObserver.observe(calendar, {
    childList: true,
    subtree: true
  });

  log('✓ Calendar observer set up');
}

// ============================================================================
// PERSISTENT IFRAME WATCHER
// Detects when BTA rebuilds its widget and re-attaches the click listener.
// This fixes the "hit or miss" behaviour when returning to the page or after
// BTA resets internally (e.g. after emptying cart and coming back).
// ============================================================================

function startIframeWatcher() {
  if (state.watcherInterval) return; // Already running

  log('Starting persistent iframe watcher');

  state.watcherInterval = setInterval(() => {
    const doc = getIframeDoc();
    if (!doc) return;

    // Check if the calendar is present in this document
    const calendar = doc.querySelector('table, .rdp, [class*="calendar"]');
    if (!calendar) return;

    // If this doc is different from the one we last attached a listener to,
    // the widget has been rebuilt — re-attach everything.
    if (doc !== state.lastListenerDoc) {
      log('📡 Watcher: iframe document changed - re-attaching listeners');

      // v4.3 telemetry: a rebuild that wipes an in-progress selection is a
      // prime suspect for the visible "calendar jump" reports
      const midSelection = state.clickCount > 0 || !!timeState.dropOffDate || !!timeState.pickUpDate;
      if (midSelection) {
        telNotable('watcher_reset_mid_selection', {
          clickCount: state.clickCount,
          hadDropOff: !!timeState.dropOffDate,
          hadPickUp: !!timeState.pickUpDate
        });
      } else {
        telEvent('watcherReattach', null);
      }

      state.lastListenerDoc = doc;

      // Reset booking state since the widget has restarted
      state.clickCount = 0;
      state.firstSelectedDate = null;
      state.soldOutDatesInRange = [];

      // v4.2 FIX: also clear time/date selections so a stale drop-off from
      // the pre-rebuild session can't pair with a fresh post-rebuild pick-up
      resetTimeSelections();

      // Reset Flex state so the modal shows again for the new booking session
      flexState.reset();

      // Reset Flex state so modal shows again for new booking attempt
      flexState.decision = null;
      flexState.shown = false;

      installEventListeners(doc);
      setupCalendarObserver(doc);
      injectAvailabilityBadge(doc);
      injectWaitlistButton(doc);
      injectMonthDropdown(doc);
      // Note: initializeFormInterception is NOT called here because it manages
      // its own interval (state.nextButtonInterval) which runs continuously.
      // The checkForNextButton loop already handles iframe rebuilds by getting
      // a fresh doc reference on every tick.
      log('✓ Watcher: listeners re-attached successfully');
    }
  }, 250); // v4.7: was 1000ms - shorter gap for missed taps after a rebuild; still just a doc identity check

  log('✓ Iframe watcher started (checking every 250ms)');
}


// ============================================================================
// INITIALIZATION
// ============================================================================

function init() {
  log('═══════════════════════════════════════════════════════════');
  log('INITIALIZATION STARTED');
  log('═══════════════════════════════════════════════════════════');
  log('Current URL:', window.location.href);
  log('Current pathname:', window.location.pathname);

  let attempts = 0;
  const maxAttempts = 28;

  const tryInit = () => {
    attempts++;
    log(`─────────────────────────────────────────────────────────`);
    log(`Initialization attempt ${attempts}/${maxAttempts}`);

    const doc = getIframeDoc();
    if (!doc) {
      log(`❌ Iframe document not available yet`);
      if (attempts < maxAttempts) {
        log(`  Retrying in 500ms...`);
        setTimeout(tryInit, 500);
      } else {
        log(`❌ Max attempts reached - giving up`);
        // Hide any loading overlays so the page isn't stuck
        const overlays = document.querySelectorAll('#preparing-cart-overlay, #unified-cart-overlay, #finalizing-cart-overlay');
        overlays.forEach(el => el.remove());
      }
      return;
    }

    log(`✓ Iframe document found (attempt ${attempts})`);

    log('Looking for calendar element...');
    const calendar = doc.querySelector('table, .rdp, [class*="calendar"]');

    if (!calendar && attempts < maxAttempts) {
      log('❌ Calendar not found yet');
      if (attempts % 10 === 0) {
        log(`  Still waiting for calendar... (${attempts}/${maxAttempts})`);
      }
      setTimeout(tryInit, 500);
      return;
    }

    if (!calendar) {
      log('❌ ERROR: Calendar not found after timeout');
      const overlays = document.querySelectorAll('#preparing-cart-overlay, #unified-cart-overlay, #finalizing-cart-overlay');
      overlays.forEach(el => el.remove());
      return;
    }

    log('✓ Calendar found!');
    log('  Calendar element:', calendar);
    log('  Calendar tagName:', calendar.tagName);
    log('  Calendar className:', calendar.className);

    const dateButtons = doc.querySelectorAll('button.rdp-day');
    log(`  Found ${dateButtons.length} date button(s) with class "rdp-day"`);

    if (dateButtons.length > 0) {
      log('  Sample date buttons:');
      dateButtons.forEach((btn, i) => {
        if (i < 3) {
          log(`    Button ${i + 1}: "${btn.textContent}" - classes: "${btn.className}"`);
        }
      });
    } else {
      log('  WARNING: No date buttons found yet');
    }

    log('Installing event listeners...');
    // v4.3 telemetry: how long init took to find the widget (missed-click window)
    telEvent('initComplete', { attempts: attempts });
    installEventListeners(doc);

    // Record this doc so the watcher knows not to re-attach immediately
    state.lastListenerDoc = doc;

    log('Initializing form interception...');
    initializeFormInterception();

    // Start the persistent watcher that will re-attach if BTA rebuilds its widget
    startIframeWatcher();

    log('═══════════════════════════════════════════════════════════');
    log('✓ INITIALIZATION COMPLETE');
    log('BTA Boarding script is now active and monitoring clicks');
    log('Version: 4.7.1 - Flex modal Confirm always visible');
    log('═══════════════════════════════════════════════════════════');
  };

  if (document.readyState === 'loading') {
    log('Document still loading - waiting for DOMContentLoaded');
    document.addEventListener('DOMContentLoaded', () => {
      log('DOMContentLoaded fired');
      setTimeout(tryInit, 2000);
    });
  } else {
    log('Document already loaded - starting initialization in 2s');
    setTimeout(tryInit, 2000);
  }
}

// Start the application
init();

})();